/**
 * KYND cart-drawer upsell layer — native <cart-drawer>, additive only.
 *
 * Reads Shopify Liquid/cart JSON state, matched by exact variant ID.
 * Never writes prices — every price shown comes straight from Shopify's
 * own cart/section-rendering response. Cart mutations use the real Ajax
 * Cart API with the `sections` param so the native cart-drawer section
 * re-renders server-side (Shopify stays authoritative for prices/discounts).
 */
(function () {
  'use strict';

  var CONTAINER_ID = 'kynd-cart-upsells';
  var GIFT_PROP_KEY = '_kynd_gift';
  var GIFT_PROP_VALUE = '3_box';
  var SECTION_ID = 'cart-drawer';
  var BUBBLE_SECTION_ID = 'cart-icon-bubble';

  var reconciling = false; // reentrancy guard for auto add/remove of the gift scraper

  // The standalone /cart page (unlike every other page) renders its OWN
  // independent copy of line items / applied discounts / totals, in
  // sections separate from the (always-present-but-often-hidden)
  // cart-drawer section. Discovered dynamically via the two stable native
  // ids Shrine/Dawn always uses (#main-cart-items, #main-cart-footer)
  // rather than hardcoding this store's numeric template section id, so
  // this keeps working if that id ever changes. Empty on any page that
  // doesn't render the /cart template (product pages, collections, etc. —
  // there only cart-drawer + cart-icon-bubble apply, as before).
  var EXTRA_SECTION_SWAPS = (function () {
    var map = {};
    [
      ['main-cart-items', '#main-cart-items'],
      ['main-cart-footer', '#main-cart-footer']
    ].forEach(function (pair) {
      var el = document.getElementById(pair[0]);
      var section = el && el.closest('[id^="shopify-section-"]');
      if (section) {
        var sectionId = section.id.replace(/^shopify-section-/, '');
        map[sectionId] = pair[1];
      }
    });
    return map;
  })();

  function allSectionIds() {
    return [SECTION_ID, BUBBLE_SECTION_ID].concat(Object.keys(EXTRA_SECTION_SWAPS));
  }

  function cfg() {
    var el = document.getElementById(CONTAINER_ID);
    if (!el) return null;
    return {
      el: el,
      toothbrushVariantId: parseInt(el.getAttribute('data-toothbrush-variant-id'), 10),
      toothbrushVariantIds: (el.getAttribute('data-toothbrush-variant-ids') || el.getAttribute('data-toothbrush-variant-id') || '').split(',').map(Number).filter(Boolean),
      scraperVariantId: parseInt(el.getAttribute('data-scraper-variant-id'), 10),
      scraperProductId: parseInt(el.getAttribute('data-scraper-product-id'), 10),
      // Real Shopify product data (image/price), rendered server-side in
      // kynd-cart-upsells.liquid, so the upsell cards below can show
      // actual product imagery instead of any hardcoded asset.
      scraperImage: el.getAttribute('data-scraper-image') || '',
      scraperTitle: el.getAttribute('data-scraper-title') || 'KYND Pure Copper Tongue Scraper',
      toothbrushImage: el.getAttribute('data-toothbrush-image') || '',
      toothbrushUrl: el.getAttribute('data-toothbrush-url') || '',
      scraperUrl: el.getAttribute('data-scraper-url') || '',
      // Real Shopify-localized reference prices (cents), already rendered
      // server-side by kynd-cart-upsells.liquid — used only for display
      // (the "save" amount on the scraper upsell copy), never for
      // mutation. null when absent/invalid, never defaulted to 0.
      scraperPriceCents: parseInt(el.getAttribute('data-scraper-price'), 10) || null,
      scraperCompareAtPriceCents: parseInt(el.getAttribute('data-scraper-compare-price'), 10) || null
    };
  }

  function routes() {
    var r = window.routes || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      change: r.cart_change_url || '/cart/change.js',
      update: r.cart_update_url || '/cart/update.js',
      cartJson: '/cart.js'
    };
  }

  // Share concurrent reads from native events and discount enhancement.
  //
  // `cache: 'no-store'` is deliberate: a plain fetch() with the browser's
  // default cache mode is permitted to satisfy this GET from an HTTP-level
  // cache (disk cache, back/forward cache — most aggressive on Safari)
  // without hitting the network at all. Clearing the in-memory `cartRead`
  // promise-sharing variable in mutateCart() only prevents reusing an
  // in-flight REQUEST; it does nothing about the browser's own HTTP cache
  // satisfying a brand-new request with a stale response. Every
  // reconciliation decision is only as fresh as this read, so it must
  // always hit the real server.
  var cartRead = null;
  function fetchCart() {
    if (cartRead) return cartRead;
    var request = fetch(routes().cartJson, { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
    cartRead = request;
    function clearRead() { if (cartRead === request) cartRead = null; }
    request.then(clearRead, clearRead);
    return request;
  }

  // POSTs to the Ajax Cart API with the section-rendering params attached,
  // swaps the returned HTML into the live DOM, and resolves with the parsed
  // cart. This is how Shopify stays authoritative for prices/discounts/
  // totals — we never compute or display them ourselves.
  //
  // __kyndOwnMutation is a marker read only by our own fetch wrap below (see
  // "Wiring"); it is not a real fetch option and has no effect on the actual
  // HTTP request. It tells the wrap this call already has its own
  // .then(refresh) chain, so the wrap should not fire a second, redundant
  // refresh on top of it.
  //
  // `suppressRender` (default false): skip requesting/applying section HTML
  // for this ONE mutation. Used only for a mutation that is a known
  // INTERMEDIATE step of a larger sequence the caller will follow with
  // exactly one more, unsuppressed render once the real end state is
  // confirmed (a stale scraper line removed before its replacement is
  // added, a fresh add whose discount hasn't settled yet). Never used for a
  // mutation that is itself the final, user-visible outcome. This is what
  // stops the customer from ever seeing a scraper line flash at full price,
  // vanish, and reappear discounted mid-reconciliation — Shopify still
  // performs the real mutation and we still read its real result on the
  // next unsuppressed call, we just don't paint the known-transient
  // intermediate response.
  function mutateCart(url, body, suppressRender) {
    cartRead = null; // A subsequent read must not reuse a pre-mutation request.
    body = body || {};
    if (!suppressRender) {
      body.sections = allSectionIds();
      body.sections_url = window.location.pathname;
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      __kyndOwnMutation: true
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) {
            var message =
              (data && (data.description || data.message || data.error)) ||
              'Cart request failed (' + r.status + ')';
            var err = new Error(message);
            err.status = r.status;
            err.body = data;
            throw err;
          }
          return data;
        });
      })
      .then(function (data) {
        if (data && data.sections) {
          renderSections(data.sections);
        }
        return data;
      });
  }

  // Inner-content swaps only (never replace the outer <cart-drawer> node or
  // its wrapper) so the drawer's open/active state, focus trap and close button
  // are left untouched — mirrors the native theme's own re-render pattern
  // (it also extracts an inner selector rather than swapping whole nodes).
  var CART_DRAWER_SWAP_TARGETS = [
    '#CartDrawer .drawer__heading',
    '#CartDrawer .drawer__inner-empty',
    '#CartDrawer .cart-drawer__body',
    '#CartDrawer .drawer__footer'
  ];

  // The theme already ships a shared symbol-shrinking pass (see the
  // inline script at the bottom of layout/theme.liquid, exposed as
  // window.shrinkSymbols) that wraps €/%/£/$/@/+ characters in a
  // smaller span so they don't render oversized in Vividly/FIBRE. It
  // runs on a MutationObserver watching the whole document, which
  // already fires for our DOM swaps below — this call is a deterministic
  // backstop on top of that (idempotent: it no-ops on text already
  // wrapped), not a second implementation of the same thing.
  function refreshSymbolShrink() {
    var drawer = document.getElementById('CartDrawer');
    if (drawer && typeof window.shrinkSymbols === 'function') {
      window.shrinkSymbols(drawer);
    }
  }

  // .cart-drawer__body (one of CART_DRAWER_SWAP_TARGETS) is replaced whole
  // on every render, which destroys and recreates the shipping progress
  // bar's fill div along with everything else in it. The fresh div's
  // width comes from Liquid's own live goal_progress calculation (see
  // cart-progress-bar.liquid) and is always correct — but a brand-new DOM
  // node has no prior computed width for its `transition: width` to
  // animate FROM, so the correct new percentage just snaps into place
  // with no visible fill motion, which reads as "the bar looks static"
  // even though the number behind it is right. Replaying the old width
  // for one frame before the new (already-correct) target width applies
  // restores the animation.
  function preserveProgressBarAnimation(swap) {
    var bar = document.querySelector('#CartDrawer .cart-progress__bar__progress');
    var prevWidth = bar ? bar.style.width : null;
    swap();
    if (!prevWidth) return;
    var newBar = document.querySelector('#CartDrawer .cart-progress__bar__progress');
    if (!newBar) return;
    var targetWidth = newBar.style.width;
    if (targetWidth === prevWidth) return;
    newBar.style.transition = 'none';
    newBar.style.width = prevWidth;
    // eslint-disable-next-line no-unused-expressions
    newBar.offsetWidth; // force a reflow so the browser registers prevWidth before animating
    newBar.style.transition = '';
    requestAnimationFrame(function () {
      if (newBar.style.width === prevWidth) newBar.style.width = targetWidth;
    });
  }

  function renderSections(sections) {
    if (sections[SECTION_ID]) {
      var doc = new DOMParser().parseFromString(sections[SECTION_ID], 'text/html');
      var freshDrawer = doc.querySelector('#CartDrawer .drawer__header');
      var liveDrawer = document.querySelector('#CartDrawer .drawer__header');
      if (freshDrawer && liveDrawer) {
        var empty = freshDrawer.dataset.kyndCount === '0';
        liveDrawer.dataset.kyndCount = freshDrawer.dataset.kyndCount;
        liveDrawer.closest('cart-drawer').classList.toggle('is-empty', empty);
      }
      preserveProgressBarAnimation(function () {
        CART_DRAWER_SWAP_TARGETS.forEach(function (selector) {
          var freshEl = doc.querySelector(selector);
          var liveEl = document.querySelector(selector);
          if (freshEl && liveEl) liveEl.innerHTML = freshEl.innerHTML;
        });
      });
    }
    if (sections[BUBBLE_SECTION_ID]) {
      var bubbleWrapperId = 'shopify-section-' + BUBBLE_SECTION_ID;
      var bubbleDoc = new DOMParser().parseFromString(sections[BUBBLE_SECTION_ID], 'text/html');
      var freshBubble = bubbleDoc.getElementById(bubbleWrapperId) || bubbleDoc.body.firstElementChild;
      var liveBubble = document.getElementById(bubbleWrapperId);
      if (freshBubble && liveBubble) liveBubble.innerHTML = freshBubble.innerHTML;
    }
    // The /cart page's own cart-items / cart-footer sections, when present
    // (see EXTRA_SECTION_SWAPS above) — same inner-content-only pattern.
    Object.keys(EXTRA_SECTION_SWAPS).forEach(function (sectionId) {
      if (!sections[sectionId]) return;
      var selector = EXTRA_SECTION_SWAPS[sectionId];
      var extraDoc = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      var freshExtra = extraDoc.querySelector(selector);
      var liveExtra = document.querySelector(selector);
      if (freshExtra && liveExtra) liveExtra.innerHTML = freshExtra.innerHTML;
    });
    refreshSymbolShrink();
    // Every section swap here — whether triggered by a native event or one
    // of our OWN reconciliation mutations — replaces .drawer__footer's
    // innerHTML wholesale, destroying whatever discount-form/-collapse/
    // -badge enhancements were previously attached to it (their dataset
    // guards live on the OLD, now-discarded elements; the fresh server
    // HTML has none). enhanceDrawerUI() was previously wired only to the
    // native-signal listeners, so any section swap caused by OUR OWN
    // mutateCart() calls (e.g. a main-offer retag, a scraper settlement
    // nudge) left the drawer with a stale, no-longer-removable discount
    // badge and no way to reattach it until the next NATIVE event happened
    // to fire. Calling it here, once, at the single chokepoint every swap
    // already funnels through, closes that gap regardless of source —
    // each of the three functions it calls already guards against
    // re-wiring the SAME still-live element more than once.
    enhanceDrawerUI();
  }

  function matchesVariant(line, ids) {
    return Array.isArray(ids) ? ids.indexOf(line.variant_id) !== -1 : line.variant_id === ids;
  }

  function brushIds(c) { return c.toothbrushVariantIds || c.toothbrushVariantId; }

  function sumQty(cart, variantId) {
    return cart.items
      .filter(function (i) {
        return matchesVariant(i, variantId);
      })
      .reduce(function (sum, i) {
        return sum + i.quantity;
      }, 0);
  }

  // Same as sumQty but excludes subscription (selling-plan) lines. Gift
  // eligibility is about a genuine one-time 3-box purchase — a cart with
  // only subscription boxes (or a mix that doesn't reach 3 one-time boxes)
  // must not trigger the free gift.
  function sumOneTimeQty(cart, variantId) {
    return cart.items
      .filter(function (i) {
        return matchesVariant(i, variantId) && !i.selling_plan_allocation;
      })
      .reduce(function (sum, i) {
        return sum + i.quantity;
      }, 0);
  }

  function hasSellingPlan(cart, variantId) {
    return cart.items.some(function (i) {
      return matchesVariant(i, variantId) && !!i.selling_plan_allocation;
    });
  }

  // A scraper line's properties matching Rapi's own NATIVE free-gift shape
  // — captured live: {id, offer, gift:true} (no upsell/upid) — distinct
  // from both the main line's shape (no gift/upsell flag at all) and the
  // paid-upsell shape (upsell:true/upid instead of gift:true).
  function isRapiNativeGiftProperties(properties) {
    if (!properties || !properties.__rapi_offer) return false;
    try {
      var parsed = JSON.parse(properties.__rapi_offer);
      return !!(parsed && parsed.gift === true);
    } catch (e) {
      return false;
    }
  }

  // Positively identifies ONLY the toothbrush-tier $9.95/€14.95 scraper
  // upsell shape — {id, offer, upsell:true, upid} per rapiScraperUpsellProperties
  // — never a normal scraper-bundle MAIN line. FIXED (live bug): the scraper
  // PDP's own Rapi quantity-break bundle (e.g. "Full House", 4 units, 20%
  // off) tags its line with __rapi_offer too — {main:true, id, offer}, same
  // shape rapiMainOfferProperties uses for the toothbrush's own main line —
  // so bare "__rapi_offer exists" was never sufficient to mean "ours".
  // upsell:true is the one field Rapi's own native upsell-add and this
  // file's own rapiScraperUpsellProperties both set, and that a MAIN-shaped
  // line (any product) never has — confirmed against live cart JSON for
  // both a genuine toothbrush-tier upsell line and a normal scraper
  // quantity-break line.
  function isRapiScraperUpsellProperties(properties) {
    if (!properties || !properties.__rapi_offer) return false;
    try {
      var parsed = JSON.parse(properties.__rapi_offer);
      return !!(parsed && parsed.upsell === true);
    } catch (e) {
      return false;
    }
  }

  // Recognizes EITHER gift marker: the legacy _kynd_gift property (still
  // honored so an existing gift line from before this fix, or one added by
  // reconcileGiftScraper's own fallback below, is never duplicated) or
  // Rapi's own native gift shape (what new gift lines are tagged with —
  // see rapiGiftScraperProperties / reconcileThreeBoxGift below).
  function findGiftScraperLine(cart, scraperVariantId) {
    return cart.items.find(function (i) {
      if (i.variant_id !== scraperVariantId) return false;
      if (i.properties && i.properties[GIFT_PROP_KEY] === GIFT_PROP_VALUE) return true;
      return isRapiNativeGiftProperties(i.properties);
    });
  }

  // Presentation follows Shopify's actual line totals, regardless of who added
  // the scraper. The private gift property still controls reconciliation only.
  function hasFreeGiftScraper(cart, scraperVariantId) {
    return cart.items.some(function (line) {
      return line.variant_id === scraperVariantId && line.quantity > 0 &&
        line.final_line_price === 0;
    });
  }

  function hasPaidScraper(cart, scraperVariantId) {
    return cart.items.some(function (line) {
      return line.variant_id === scraperVariantId && line.final_line_price > 0;
    });
  }

  function findUpgradeableToothbrushLine(cart, toothbrushVariantId, subscriptionTarget) {
    if (subscriptionTarget) {
      var brushLines = cart.items.filter(function (line) { return matchesVariant(line, toothbrushVariantId); });
      // Do not silently switch line, purchase type, frequency, or target tier if
      // the cart changed while this offer was visible.
      var samePlan = brushLines.length && brushLines.every(function (line) {
        var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
        return plan && String(plan.id) === String(subscriptionTarget.planId);
      });
      if (!samePlan || sumQty(cart, toothbrushVariantId) !== subscriptionTarget.quantity) return null;
      return brushLines.find(function (line) { return line.key === subscriptionTarget.lineKey; });
    }
    // Preserve the existing one-time selection path.
    return cart.items.find(function (i) {
      return matchesVariant(i, toothbrushVariantId) && !i.selling_plan_allocation;
    });
  }

  // Commercial ladder (1 box full price / 2 boxes 10% / 3 boxes 15% +
  // free scraper + free shipping) drives a `kind` per box quantity:
  //  - qty 1            -> 'add-box'       (+ paid scraper offer if absent)
  //  - qty 2, no scraper -> 'upgrade-basic' (+ paid scraper offer, possibly
  //                          framed as unlocking free shipping)
  //  - qty 2, scraper    -> 'upgrade-strong' (strongest state, no secondary)
  //  - qty >=3           -> 'success'       (completed-ladder badges only)
  //  - subscription qty 1/2 -> change the existing line; never add a brush line.
  //    One-time/subscription mixtures or different selling plans remain excluded
  //    because a cart-wide CTA cannot choose a customer's renewal frequency.
  function hasAllocatedTier(line, title) {
    return (line.line_level_discount_allocations || []).some(function (allocation) {
      var discount = allocation.discount_application || {};
      return allocation.amount > 0 && discount.title === title && discount.type === 'automatic';
    });
  }

  function scraperUpgradeTarget(cart, c) {
    var lines = cart.items.filter(function (line) { return line.variant_id === c.scraperVariantId; });
    var line = lines[0];
    if (lines.length !== 1 || !line.key || line.selling_plan_allocation || line.final_line_price <= 0 ||
        (line.properties && line.properties[GIFT_PROP_KEY] === GIFT_PROP_VALUE) || line.quantity >= 4) return null;
    return { lineKey: line.key, quantity: line.quantity, target: line.quantity === 1 ? 2 : 4 };
  }

  function mixedScraperUpgradeTarget(cart, c) {
    // Merchant QA verified the one-time 3 Boxes / Full House configuration with
    // BEKYND. Do not extrapolate this evidence to other brush tiers or plans.
    var brushes = cart.items.filter(function (line) { return matchesVariant(line, brushIds(c)); });
    if (sumQty(cart, brushIds(c)) !== 3 || !brushes.every(function (line) {
      return !line.selling_plan_allocation && hasAllocatedTier(line, '3 Boxes');
    })) return null;
    var target = scraperUpgradeTarget(cart, c);
    if (!target || (target.quantity !== 1 && target.quantity !== 2)) return null;
    var line = cart.items.find(function (item) { return item.key === target.lineKey; });
    // Two-unit source must match the existing scraper ladder. A single paid
    // scraper has no quantity tier yet; exclude other line-level promotions.
    if (target.quantity === 2) {
      var tierAmount = (line.line_level_discount_allocations || []).reduce(function (n, a) {
        return n + ((a.discount_application || {}).title === 'You & Me' ? a.amount : 0);
      }, 0);
      if (!hasAllocatedTier(line, 'You & Me') || !(line.original_line_price > 0) ||
          Math.abs(tierAmount / line.original_line_price - 0.10) > 0.005) return null;
    }
    if (target.quantity === 1 && (line.line_level_discount_allocations || []).some(function (a) { return a.amount > 0; })) return null;
    return target;
  }

  function scraperUpgradeOffer(target, c, cart) {
    var toQty = target.quantity === 1 ? 2 : 4;
    var display = rapiScraperTierUpgradeDisplay(c, target.quantity, toQty);
    var title = target.quantity === 1 ? 'Make it two' : 'Go Full House';
    var ctaBase = target.quantity === 1 ? 'Upgrade to 2' : 'Upgrade to 4';
    var subtitle, ctaLabel;
    if (display != null) {
      var deltaText = formatMoney(display.deltaCents, cart);
      subtitle = 'Only ' + deltaText + ' more · Save ' + formatRapiPercent(display.percentage) +
        (target.quantity === 2 ? ' + FREE shipping' : '');
      ctaLabel = ctaBase + ' · ' + deltaText;
    } else {
      // Fails open to the existing generic copy whenever the live delta
      // can't be safely resolved — never a hardcoded price/percentage.
      subtitle = target.quantity === 1 ? 'Keep one at home and one spare.' : 'Stock up on copper scrapers for your household.';
      ctaLabel = ctaBase;
    }
    return {
      role: 'scraper-upgrade', image: c.scraperImage, url: c.scraperUrl,
      title: title, subtitle: subtitle, ctaLabel: ctaLabel, action: 'upgrade-scrapers'
    };
  }

  // Whether Rapi's CURRENTLY CONFIGURED offer for this exact box quantity
  // has its own paid scraper upsell — resolved fresh from the live
  // embedded config every call via the SAME resolveRapiScraperUpsell()
  // every other paid-scraper flow (1/2-box CTA, retag reconciliation)
  // already uses. Never assumes a tier does or doesn't have one; never
  // invents a match. Works unmodified whether Rapi has this configured for
  // 1, 2, 3, or no tiers at all — nothing here is tier-specific.
  function hasAvailablePaidScraperUpsell(cart, c, boxQty) {
    var productId = primaryToothbrushProductId(cart, c);
    return !!productId && !!resolveRapiScraperUpsell(boxQty, productId, c.scraperProductId);
  }

  function getState(cart, c) {
    var boxQty = sumQty(cart, brushIds(c));
    var scraperQty = sumQty(cart, c.scraperVariantId);
    var isSubscription = hasSellingPlan(cart, brushIds(c));

    var state = {
      boxQty: boxQty, scraperQty: scraperQty, kind: 'none',
      hasPaidScraper: hasPaidScraper(cart, c.scraperVariantId)
    };

    // Scraper-origin carts share this decision layer and the existing renderer.
    if (boxQty === 0 && scraperQty > 0) {
      state.kind = 'scraper-first';
      var scraperLines = cart.items.filter(function (line) { return line.variant_id === c.scraperVariantId; });
      state.scraperTarget = scraperUpgradeTarget(cart, c);
      // No target-cart quote exists. Benefits below describe only actual allocations.
      var original = scraperLines.reduce(function (n, line) { return n + (line.original_line_price || 0); }, 0);
      var final = scraperLines.reduce(function (n, line) { return n + line.final_line_price; }, 0);
      var allocated = scraperLines.every(function (line) {
        var tierAmount = (line.line_level_discount_allocations || []).reduce(function (amount, allocation) {
          return amount + (/scraper|bundle/i.test((allocation.discount_application || {}).title || '') ? allocation.amount : 0);
        }, 0);
        return line.original_line_price > 0 && Math.abs(tierAmount / line.original_line_price - 0.20) <= 0.005;
      });
      state.scraperBestValue = scraperQty >= 4 && allocated && original > 0 &&
        Math.abs((original - final) / original - 0.20) <= 0.005 && isFreeShippingReached();
      state.offers = [];
      // The toothbrush cross-sell is only ever shown when Rapi's OWN
      // currently-configured scraper offer (for the exact scraper quantity
      // in cart) actually has a cross-product upsell configured — resolved
      // fresh every call, never assumed. Rapi's published FULL HOUSE tier
      // (qty 4) has none, so this correctly stays hidden there without any
      // quantity-specific special-casing in this file.
      if (resolveScraperCrossSellUpsell(scraperQty, c)) {
        var handoffSavingCents = scraperQty === 1 ? resolveScraperHandoffSavingCents(c) : null;
        state.offers.push({
          role: 'brush-cross-sell', image: c.toothbrushImage, url: c.toothbrushUrl,
          title: 'Add 4 plastic-free toothbrushes',
          subtitle: handoffSavingCents != null ? 'Add the brushes + save ' + formatMoney(handoffSavingCents, cart) + ' on your scraper.' :
            scraperQty === 1 ? 'One box = a full year of KYND brushing.' :
            scraperQty >= 4 ? 'Complete your KYND oral-care routine.' : 'Complete your plastic-free oral-care routine.',
          ctaLabel: 'Add toothbrushes', action: 'add-toothbrushes'
        });
      }
      if (state.scraperTarget) {
        var offer = scraperUpgradeOffer(state.scraperTarget, c, cart);
        if (scraperQty === 3) state.offers.unshift(offer);
        else state.offers.push(offer);
      }
      return state;
    }

    state.hasBundleSavings = cart.items.some(function (line) {
      return matchesVariant(line, brushIds(c)) && hasAllocatedTier(line, '3 Boxes');
    });
    state.scraperTarget = mixedScraperUpgradeTarget(cart, c);

    if (isSubscription) {
      if (boxQty === 1 || boxQty === 2) {
        var brushLines = cart.items.filter(function (line) { return matchesVariant(line, brushIds(c)); });
        var firstLine = brushLines[0];
        var firstPlan = firstLine.selling_plan_allocation && firstLine.selling_plan_allocation.selling_plan;
        if (firstLine.key && firstPlan && firstPlan.id && brushLines.every(function (line) {
          var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
          return plan && String(plan.id) === String(firstPlan.id);
        })) {
          state.kind = boxQty === 1 ? 'add-box' : 'upgrade-strong';
          state.subscriptionTarget = { lineKey: firstLine.key, planId: firstPlan.id, quantity: boxQty };
          // Scraper is a separate one-time add; any existing scraper satisfies this offer.
          state.showScraperOffer = scraperQty === 0;
        }
      } else if (boxQty >= 3) {
        state.kind = 'success';
        // Rapi support confirms a scraper without a subscription is initial-order
        // only. Display still follows actual current-cart allocations, not intent.
        state.isSubscriptionInitialOrder = true;
        state.hasGiftScraper = hasFreeGiftScraper(cart, c.scraperVariantId);
        state.hasRealDiscount = boxDiscountRateMatches(cart, c, THREE_BOX_DISCOUNT_RATE);
        state.freeShippingReached = isFreeShippingReached();
        // Only offer the additional paid scraper once the free gift is
        // GENUINELY confirmed free in cart (hasGiftScraper, not merely
        // "boxQty >= 3"), only if one hasn't already been added, and only
        // if Rapi's live config genuinely has a paid upsell for this tier.
        state.showScraperOffer = state.hasGiftScraper && !findPaidScraperLine(cart, c) &&
          hasAvailablePaidScraperUpsell(cart, c, boxQty);
      }
      return state;
    }

    if (boxQty === 1) {
      state.kind = 'add-box';
      // Live Rapi config now configures TWO scraper upsells on this tier
      // (confirmed: position 1 = EUR14.95, position 2 = EUR9.95) — shows
      // the offer as long as ANY configured upsell isn't yet represented
      // by a real line, not just when scraperQty is exactly 0. Resolves
      // fresh every call; never assumes how many upsells exist.
      state.showScraperOffer = !!resolveNextRapiScraperUpsell(cart, c, boxQty);
    } else if (boxQty === 2) {
      if (scraperQty === 0) {
        state.kind = 'upgrade-basic';
        state.freeShippingUnlock = willUnlockFreeShipping(cart, c);
      } else {
        state.kind = 'upgrade-strong';
      }
      // Same live, multi-upsell-aware gate as the 1-box tier above, for
      // BOTH sub-states (upgrade-basic and upgrade-strong) — the existing
      // scraperQty branch above only decides which eyebrow/CTA framing to
      // use, not whether a scraper offer exists.
      state.showScraperOffer = !!resolveNextRapiScraperUpsell(cart, c, boxQty);
    } else if (boxQty >= 3) {
      state.kind = 'success';
      state.hasGiftScraper = hasFreeGiftScraper(cart, c.scraperVariantId);
      // Gated on the REAL applied box discount actually matching the
      // claimed 15% — not just "some discount exists". Before the Rapi
      // cutover the live discount on this store is a different rate
      // (confirmed ~22% in testing), so this correctly stays false and
      // the "15% SAVED" badge stays hidden until the real rate matches.
      // No further code change needed post-cutover: once Rapi actually
      // applies 15%, this starts passing on its own.
      state.hasRealDiscount = boxDiscountRateMatches(cart, c, THREE_BOX_DISCOUNT_RATE);
      // Real threshold state (native progress bar), never inferred from
      // box quantity alone — see isFreeShippingReached().
      state.freeShippingReached = isFreeShippingReached();
      // Restored: the tier-3 paid "Add another copper scraper" CTA offers
      // Rapi's single configured tier-3 upsell exactly ONCE — shown only
      // once the free gift is GENUINELY confirmed free in cart
      // (hasGiftScraper, not merely "boxQty >= 3"), only while no paid
      // scraper line exists yet, and only if Rapi's live config genuinely
      // has a paid upsell for this tier. Unlike the retired "unlimited"
      // attempt, this never re-offers the slot once it's filled — native
      // "+" stays hidden/disabled on both the gift and the paid line (see
      // interceptScraperQuantityIncrement / cart-drawer.liquid's
      // kynd_is_scraper_line), so tier 3's maximum is exactly 1 free + 1
      // optional paid scraper. Removing the paid line naturally reopens
      // this gate again via findPaidScraperLine.
      state.showScraperOffer = state.hasGiftScraper && !findPaidScraperLine(cart, c) &&
        hasAvailablePaidScraperUpsell(cart, c, boxQty);
    }
    return state;
  }

  // --- Pricing helpers for the new ladder's copy. Every input is real,
  // live data from /cart.js (or, for the not-yet-in-cart scraper, the
  // actual product price rendered server-side into data-scraper-price —
  // see kynd-cart-upsells.liquid). Nothing here writes or invents a price;
  // Shopify remains authoritative once a customer actually clicks a CTA.

  var THREE_BOX_DISCOUNT_RATE = 0.15; // the PLANNED 3-box ladder tier.
  // Used only to PROJECT what upgrading to 3 boxes would cost before the
  // customer clicks — there is no cart "preview" API to ask Shopify/Rapi
  // for that number without actually applying the change. The two costs
  // it's compared against below (current 2-box line price, current
  // scraper line price) are always read live from cart.js, never invented.
  //
  // *** PENDING POST-CUTOVER VALIDATION ***
  // This formula is preserved as-is (not modified per instruction) but the
  // number it currently produces is computed against whatever discount is
  // LIVE on the store today, which — until the new Rapi 10%/15% bundle is
  // cut over — is a different, older rate. That means the displayed "for
  // only $X more" amount is not yet representative of the final ladder.
  // Do not treat it as validated. Once the real Rapi bundle goes live,
  // re-run the 2-box+scraper regression check and confirm the displayed
  // amount matches reality before treating this state as production-ready.

  // Shopify's active market money filter is the canonical display specification.
  // A non-round, grouped sample preserves its symbol, grouping and precision even
  // when the current subtotal is zero. No currency conversion or pricing occurs.
  function deriveMoneyPattern(cart) {
    var progress = document.querySelector('#CartDrawer [data-money-sample]');
    return progress ? moneyPatternFromText(progress.getAttribute('data-money-sample'), 123456789) : null;
  }

  function moneyPatternFromText(text, knownCents) {
    text = (text || '').trim();
    var digitStream = text.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');
    if (!digitStream) return null;

    var decimalDigits = null;
    for (var d = 4; d >= 0; d--) {
      var candidateStr = String(Math.round((Math.abs(knownCents) / 100) * Math.pow(10, d)));
      if (digitStream === candidateStr) {
        decimalDigits = d;
        break;
      }
    }
    if (decimalDigits === null) return null;

    var digitPositions = [];
    for (var i = 0; i < text.length; i++) {
      if (text[i] >= '0' && text[i] <= '9') digitPositions.push(i);
    }

    var minorStartIdx = decimalDigits > 0 ? digitPositions[digitPositions.length - decimalDigits] : null;
    var majorEndIdx =
      decimalDigits > 0
        ? digitPositions[digitPositions.length - decimalDigits - 1]
        : digitPositions[digitPositions.length - 1];

    var decimalSeparator = decimalDigits > 0 ? text.slice(majorEndIdx + 1, minorStartIdx) : '';
    var prefix = text.slice(0, digitPositions[0]);
    var suffix =
      decimalDigits > 0
        ? text.slice(digitPositions[digitPositions.length - 1] + 1)
        : text.slice(majorEndIdx + 1);

    var thousandsSeparator = '';
    for (var j = digitPositions[0]; j <= majorEndIdx; j++) {
      var ch = text[j];
      if (ch < '0' || ch > '9') {
        thousandsSeparator = ch;
        break;
      }
    }

    return {
      prefix: prefix,
      suffix: suffix,
      decimalDigits: decimalDigits,
      decimalSeparator: decimalSeparator,
      thousandsSeparator: thousandsSeparator
    };
  }

  function groupDigits(digits, separator) {
    if (!separator) return digits;
    var out = '';
    var count = 0;
    for (var i = digits.length - 1; i >= 0; i--) {
      out = digits[i] + out;
      count++;
      if (count % 3 === 0 && i !== 0) out = separator + out;
    }
    return out;
  }

  function formatWithPattern(cents, pattern) {
    var scale = Math.pow(10, pattern.decimalDigits);
    var scaled = Math.round((Math.abs(cents) / 100) * scale);
    var digits = String(scaled).padStart(pattern.decimalDigits + 1, '0');
    var minorDigits = pattern.decimalDigits > 0 ? digits.slice(-pattern.decimalDigits) : '';
    var majorDigits = pattern.decimalDigits > 0 ? digits.slice(0, -pattern.decimalDigits) || '0' : digits;
    var grouped = groupDigits(majorDigits, pattern.thousandsSeparator);
    var result = pattern.prefix + grouped;
    if (pattern.decimalDigits > 0 && Number(minorDigits) !== 0) result += pattern.decimalSeparator + minorDigits;
    result += pattern.suffix;
    return (cents < 0 ? '-' : '') + result;
  }

  function formatMoney(cents, cart) {
    var pattern = deriveMoneyPattern(cart);
    if (pattern) return formatWithPattern(cents, pattern);
    // No rendered reference amount was found to copy a pattern from (should
    // not normally happen — the subtotal always renders alongside this
    // widget). Degrade to a plain, still-honest number rather than break.
    return (cents / 100).toFixed(2);
  }

  // Read the shared Liquid threshold directly; never reverse an animated width.
  function freeShippingGoalCents(cart) {
    var progress = document.querySelector('#CartDrawer [data-shipping-threshold]');
    if (!progress) return null;
    var goal = Number(progress.dataset.shippingThreshold);
    return goal > 0 && cart.total_price < goal ? goal : null;
  }

  function updateShippingProgress(cart) {
    var progress = document.querySelector('#CartDrawer [data-shipping-threshold]');
    if (!progress || !cart || !Number.isFinite(cart.total_price)) return;
    var goal = Number(progress.dataset.shippingThreshold);
    var currency = progress.dataset.shippingCurrency;
    // A country/currency change needs fresh Liquid configuration, never FX math.
    if (!(goal > 0) || (cart.currency && cart.currency !== currency)) return;
    var remaining = Math.max(0, goal - cart.total_price);
    var reached = cart.total_price >= goal;
    var copy = progress.querySelector('.cart-progress__text');
    var bar = progress.querySelector('.cart-progress__bar__progress');
    if (!copy || !bar) return;
    var amount;
    if (!reached) {
      var pattern = deriveMoneyPattern(cart);
      amount = pattern ? formatWithPattern(remaining, pattern) : null;
    }
    progress.dataset.shippingReached = String(reached);
    var targetWidth = Math.min(100, Math.max(0, cart.total_price / goal * 100)) + '%';
    if (bar.style.width !== targetWidth) bar.style.width = targetWidth;
    // A missing presentation sample must never block the shipping state/bar update.
    if (!reached && amount === null) return;
    var text = reached ? '✓ FREE shipping unlocked' : amount + ' away from FREE shipping';
    if (copy.textContent.trim() !== text) {
      if (reached) copy.textContent = text;
      else {
        var money = document.createElement('span');
        money.textContent = amount;
        copy.replaceChildren(money, document.createTextNode(' away from FREE shipping'));
      }
    }
  }

  function willUnlockFreeShipping(cart, c) {
    var goalCents = freeShippingGoalCents(cart);
    if (goalCents === null) return false;
    var scraperPriceCents = parseInt(c.el.getAttribute('data-scraper-price'), 10);
    if (!scraperPriceCents) return false;
    var alreadyBelow = cart.total_price < goalCents;
    var wouldReach = cart.total_price + scraperPriceCents >= goalCents;
    return alreadyBelow && wouldReach;
  }

  function isFreeShippingReached() {
    var progress = document.querySelector('#CartDrawer [data-shipping-threshold]');
    if (progress) return progress.dataset.shippingReached === 'true';
    var bar = document.querySelector('#CartDrawer .cart-progress__bar__progress');
    if (!bar) return false;
    var style = bar.getAttribute('style') || '';
    var match = /width:\s*([\d.]+)%/.exec(style);
    var pct = match ? parseFloat(match[1]) : 0;
    return pct >= 100;
  }

  // Compares the REAL discount actually applied to the box line(s) against
  // a claimed rate (e.g. THREE_BOX_DISCOUNT_RATE), computed purely from
  // cart.js's own original/final line prices — never assumed from quantity
  // or from any hardcoded percentage. Used to gate "X% SAVED"-style copy so
  // it can only ever describe a discount that is genuinely active right
  // now, not the planned/target ladder. A small tolerance (0.5 percentage
  // points) absorbs normal rounding in Shopify's own discount math.
  function boxDiscountRateMatches(cart, c, claimedRate) {
    var boxLines = cart.items.filter(function (i) {
      return matchesVariant(i, brushIds(c)) && !i.selling_plan_allocation;
    });
    if (!boxLines.length) return false;
    var origTotal = 0;
    var finalTotal = 0;
    boxLines.forEach(function (l) {
      origTotal += l.original_line_price;
      finalTotal += l.final_line_price;
    });
    if (!origTotal) return false;
    var actualRate = (origTotal - finalTotal) / origTotal;
    return Math.abs(actualRate - claimedRate) <= 0.005;
  }

  // --- Dynamic upsell-copy pricing (display only). Every number below is
  // read live from the real toothbrush line's own original_price (never
  // BEKYND/final_line_price/selling-plan-discounted) and the live Rapi
  // offer config resolved fresh via resolveRapi*() above — see the
  // pricing audit this replaces (computeUpgradeDifference and its
  // hardcoded THREE_BOX_DISCOUNT_RATE projection, the only thing that
  // constant was ever used for). Nothing here writes to the cart or
  // changes offer/bundle/upid identity; it only decides what text to
  // render for an offer that reconciliation has already decided to show.

  // The real per-unit price of a ONE-TIME toothbrush line — same filter
  // (excludes selling_plan_allocation) already used by boxDiscountRateMatches
  // above, so a subscription's own recurring-plan price can never leak
  // into a projected one-time bundle total. Returns null (never 0 or a
  // guess) when no matching line exists.
  function oneTimeBoxUnitPriceCents(cart, c) {
    var line = cart.items.filter(function (i) {
      return matchesVariant(i, brushIds(c)) && !i.selling_plan_allocation;
    })[0];
    return line && line.original_price > 0 ? line.original_price : null;
  }

  // Projects what `qty` boxes would total under a given live Rapi main
  // offer — mirrors Rapi's own shipped price-calc switch exactly
  // (confirmed live in rappi-front.js: percentage subtracts a % of the
  // base, default leaves it unchanged). Only ever a display PROJECTION —
  // never sent to Shopify, never compared against BEKYND/final_line_price.
  // Returns null for any discount_type this store's config doesn't
  // actually use for main offers (never guesses a formula for it).
  function rapiTierTotalCents(unitPriceCents, qty, offer) {
    if (!(unitPriceCents > 0) || !(qty > 0) || !offer) return null;
    var base = unitPriceCents * qty;
    if (offer.discountType === 'default') return Math.round(base);
    if (offer.discountType === 'percentage' && typeof offer.discountValue === 'number' && offer.discountValue >= 0) {
      return Math.round(base * (1 - offer.discountValue / 100));
    }
    return null;
  }

  // The live "Only [X] more" delta plus the destination tier's own real
  // percentage (for "Save N%" copy) — one call covers both, so a caller
  // can never show a delta and a percentage resolved from two different
  // moments/config reads. Returns null (fail open to existing generic
  // copy) whenever the toothbrush line, either tier's offer, or the
  // destination tier's percentage-type discount isn't cleanly resolvable.
  function rapiTierUpgradeDisplay(cart, c, fromQty, toQty) {
    var productId = primaryToothbrushProductId(cart, c);
    var unitPriceCents = oneTimeBoxUnitPriceCents(cart, c);
    if (!productId || unitPriceCents == null) return null;
    var fromOffer = resolveRapiMainOffer(fromQty, productId);
    var toOffer = resolveRapiMainOffer(toQty, productId);
    if (!fromOffer || !toOffer || toOffer.discountType !== 'percentage' || typeof toOffer.discountValue !== 'number') return null;
    var fromTotal = rapiTierTotalCents(unitPriceCents, fromQty, fromOffer);
    var toTotal = rapiTierTotalCents(unitPriceCents, toQty, toOffer);
    if (fromTotal == null || toTotal == null) return null;
    if ((cart.cart_level_discount_applications || []).length ||
        (cart.discount_codes || []).some(function (code) { return code.applicable; }) ||
        cart.items.some(function (line) {
          return (line.line_level_discount_allocations || []).some(function (allocation) {
            return (allocation.discount_application || {}).type === 'discount_code';
          });
        })) return null;
    var deltaCents = toTotal - fromTotal;
    if (fromQty === 2 && toQty === 3) {
      var brushes = cart.items.filter(function (line) { return matchesVariant(line, brushIds(c)); });
      if (brushes.length !== 1 || brushes[0].selling_plan_allocation || brushes[0].quantity !== fromQty ||
          brushes[0].final_line_price !== fromTotal) return null;
      var paidScrapers = findPaidScraperLines(cart, c);
      if (paidScrapers.length) {
        var paidScraper = paidScrapers[0];
        var targetUpsell = resolveRapiScraperUpsell(toQty, productId, c.scraperProductId);
        var paidOffer;
        try { paidOffer = JSON.parse(paidScraper.properties.__rapi_offer); } catch (e) { return null; }
        if (paidScrapers.length !== 1 || paidScraper.quantity !== 1 || paidScraper.selling_plan_allocation ||
            !(paidScraper.final_line_price > 0) || !findRapiMainOfferId(cart, c) || findAnyGiftLine(cart, c) ||
            (targetUpsell && paidOffer.offer === targetUpsell.offerIdentifier && paidOffer.upid === targetUpsell.upsellIdentifier)) return null;
        deltaCents -= paidScraper.final_line_price;
      }
    }
    if (!(deltaCents > 0)) return null;
    return { deltaCents: deltaCents, percentage: toOffer.discountValue };
  }

  // Same live delta+percentage projection as rapiTierUpgradeDisplay above,
  // for the SCRAPER's own quantity-break bundle instead of the toothbrush's
  // — reuses the exact same generic resolveRapiMainOffer/rapiTierTotalCents
  // this file already trusts for the toothbrush side, just pointed at
  // c.scraperProductId/c.scraperPriceCents. No second pricing system, no
  // new fetch: the scraper's own localized retail price is already
  // rendered server-side (data-scraper-price), exactly like every other
  // scraper price this file reads.
  function rapiScraperTierUpgradeDisplay(c, fromQty, toQty) {
    var unitPriceCents = c.scraperPriceCents;
    if (!(unitPriceCents > 0)) return null;
    var fromOffer = resolveRapiMainOffer(fromQty, c.scraperProductId);
    var toOffer = resolveRapiMainOffer(toQty, c.scraperProductId);
    if (!fromOffer || !toOffer || toOffer.discountType !== 'percentage' || typeof toOffer.discountValue !== 'number') return null;
    var fromTotal = rapiTierTotalCents(unitPriceCents, fromQty, fromOffer);
    var toTotal = rapiTierTotalCents(unitPriceCents, toQty, toOffer);
    if (fromTotal == null || toTotal == null) return null;
    var deltaCents = toTotal - fromTotal;
    if (!(deltaCents > 0)) return null;
    return { deltaCents: deltaCents, percentage: toOffer.discountValue };
  }

  // Converts a "specific"-type Rapi upsell's raw discount_value into
  // cents — MAJOR-UNIT-TO-CENTS ONLY, never an FX conversion.
  //
  // CORRECTED (live bug: EUR 14.95 was rendering as EUR13.31, i.e.
  // 14.95 * ~0.89 — a second, wrong currency conversion). KYND runs a
  // SEPARATE Rapi bundle per market (confirmed live: bundle 185066 = EU,
  // 186518 = US, 186519 = GB, 186520 = ROW, each with its own
  // offer_upsells.discount_value). resolveRapiOfferForQuantity
  // (above) already filters to bundleVisibleInMarket(bundle, rapiMarketCode())
  // — the ONLY bundle this can ever resolve is the one configured for the
  // shopper's OWN current market. That means discount_value here is
  // ALREADY a market-local price (EU's 14.95 means EUR14.95, US's own
  // upsell record would carry its own USD value, etc.) — there is no
  // second currency to convert FROM.
  //
  // This mirrors Rapi's own shipped pricing switch exactly: its
  // "specific" case multiplies by Shopify.currency.rate ONLY when
  // `specificMarketNoConvert` is false (i.e. when displaying a price from
  // a bundle that is NOT the shopper's current-market one) — a case our
  // resolver structurally never produces, since it never returns a
  // cross-market bundle. Applying that same rate here unconditionally was
  // the bug: it re-converted an already-local price a second time.
  //
  // Returns null for any other discount_type — never invents a price for
  // "percentage"/"amount"/"default" upsells (this store's config doesn't
  // currently use those for offer_upsells, and this must never assume).
  function rapiSpecificOfferPriceCents(offer) {
    if (!offer || offer.discountType !== 'specific' || typeof offer.discountValue !== 'number' || !(offer.discountValue >= 0)) return null;
    return Math.round(offer.discountValue * 100);
  }

  // The scraper's own real Shopify-localized reference price for the
  // "Save [X]" phrase — prefers compare_at_price when it's a genuine
  // markdown reference (positive and above the standard price), else
  // falls back to the standard price itself (what the customer would
  // otherwise pay outside this upsell). Both already come pre-localized
  // from kynd-cart-upsells.liquid — no FX math here, ever.
  function rapiScraperReferencePriceCents(c) {
    var compare = c.scraperCompareAtPriceCents;
    var base = c.scraperPriceCents;
    if (compare && base && compare > base) return compare;
    return base || null;
  }

  // Null (never 0, never negative) unless the reference price is both
  // present and genuinely higher than what the customer would pay here.
  function rapiScraperSavingCents(paidPriceCents, c) {
    var referenceCents = rapiScraperReferencePriceCents(c);
    if (referenceCents == null || !(paidPriceCents > 0) || referenceCents <= paidPriceCents) return null;
    return referenceCents - paidPriceCents;
  }

  // "30" -> "30%", "22.5" -> "22.5%" — never "30.00%". Rounds only to kill
  // floating-point noise (e.g. 22.500000001), never to change the
  // configured value.
  function formatRapiPercent(value) {
    var rounded = Math.round(value * 100) / 100;
    return (rounded % 1 === 0 ? rounded.toFixed(0) : String(rounded)) + '%';
  }

  // Generic display-pricing resolution for a Rapi upsell of EITHER
  // discount_type this store's config now uses — mirrors Rapi's own
  // shipped price-calc branch for EACH type exactly (both cases live in
  // the SAME switch statement in rappi-front.js):
  //
  //   case "percentage": i -= i * discountValue / 100
  //   case "specific":    i = discountValue * (currency rate, or 1 when
  //                        the resolved bundle is already the shopper's
  //                        own market — see rapiSpecificOfferPriceCents)
  //
  // For "percentage", `i` there is the upsell PRODUCT'S OWN already-
  // localized Shopify variant price (never a Rapi-configured number) —
  // confirmed live: Rapi resolves the linked product's variant and reads
  // its real price/compare_at_price before applying the switch. That is
  // exactly what cfg().scraperPriceCents already is (kynd-cart-upsells.liquid's
  // data-scraper-price), so no new data source is needed. No intermediate
  // rounding happens in Rapi's own branch before its final money-format
  // step, so this rounds exactly once, at the very end, matching that
  // order — never Shopify.currency.rate (percentages apply against an
  // already-localized price, currency-agnostic by construction).
  //
  // Returns null whenever the offer is missing, its discount_type isn't
  // one this resolves (never guesses a formula for "amount"/"default" on
  // a scraper upsell — this store's config doesn't use those here), or the
  // required localized base price isn't available. Never called for a
  // genuinely free gift line — callers gate on that separately, exactly as
  // before.
  function rapiUpsellDisplayPricing(offer, c) {
    if (!offer) return null;
    if (offer.discountType === 'specific') {
      var specificCents = rapiSpecificOfferPriceCents(offer);
      if (specificCents == null) return null;
      return {
        discountType: 'specific',
        finalPriceCents: specificCents,
        discountPercent: null,
        savingsCents: rapiScraperSavingCents(specificCents, c)
      };
    }
    if (offer.discountType === 'percentage') {
      if (typeof offer.discountValue !== 'number' || !(offer.discountValue >= 0) || offer.discountValue > 100) return null;
      var basePriceCents = c.scraperPriceCents;
      if (!(basePriceCents > 0)) return null;
      var finalPriceCents = Math.round(basePriceCents * (1 - offer.discountValue / 100));
      var savingsCents = basePriceCents - finalPriceCents;
      return {
        discountType: 'percentage',
        finalPriceCents: finalPriceCents,
        discountPercent: offer.discountValue,
        savingsCents: savingsCents > 0 ? savingsCents : null
      };
    }
    return null;
  }

  // Keeps the "gift" scraper line in sync with box quantity, without ever
  // touching a scraper line the customer added themselves (no gift
  // property). Counts ALL toothbrush lines — one-time AND subscription —
  // toward the 3-box threshold: a subscription's INITIAL signup order is
  // explicitly eligible for the free gift (business requirement), not
  // just one-time purchases. The gift is always added below as a plain
  // one-time line (no selling_plan — see mkGiftProps/the add call below),
  // so this function can never itself create a recurring scraper
  // subscription. Rapi support confirms initial-order-only treatment when
  // the scraper product has no subscription configured. This function still
  // only acts on the current cart; its reconciliation rules are unchanged.
  function reconcileGiftScraper(cart, c) {
    if (reconciling) return Promise.resolve(cart);
    var boxQty = sumQty(cart, brushIds(c));
    var scraperQty = sumQty(cart, c.scraperVariantId);
    var giftLine = findGiftScraperLine(cart, c.scraperVariantId);

    if (boxQty >= 3 && scraperQty === 0 && !giftLine) {
      reconciling = true;
      // Unsuppressed (this legacy shape carries no Rapi offer/upid tag to
      // settle — it relies on a separate, genuinely-immediate Shopify BXGY
      // discount, so there's no transient state to hide here) — its own
      // response is already the fresh cart, no extra read needed.
      return mutateCart(routes().add, {
        items: [{ id: c.scraperVariantId, quantity: 1, properties: mkGiftProps() }]
      })
        .finally(function () {
          reconciling = false;
        });
    }

    if (boxQty < 3 && giftLine) {
      reconciling = true;
      return mutateCart(routes().change, { id: giftLine.key, quantity: 0 })
        .finally(function () {
          reconciling = false;
        });
    }

    return Promise.resolve(cart);
  }

  function mkGiftProps() {
    var p = {};
    p[GIFT_PROP_KEY] = GIFT_PROP_VALUE;
    return p;
  }

  // `activeBtn`/`pendingLabel` are optional: when given, ONLY that specific
  // button's own visible label is swapped ("Add scraper" -> "Adding...")
  // while busy, and restored from its own dataset on release — every OTHER
  // CTA in the card is still disabled (never a second concurrent action)
  // but keeps its own label, since it wasn't the one clicked.
  function setBusy(card, busy, activeBtn, pendingLabel) {
    if (!card) return;
    card.querySelectorAll('.kynd-upsell__cta').forEach(function (btn) {
      btn.disabled = !!busy;
    });
    card.setAttribute('aria-busy', String(!!busy));
    card.classList.toggle('kynd-upsell--busy', !!busy);
    if (!activeBtn) return;
    if (busy && pendingLabel) {
      if (activeBtn.dataset.kyndLabel == null) activeBtn.dataset.kyndLabel = activeBtn.textContent;
      activeBtn.textContent = pendingLabel;
    } else if (!busy && activeBtn.dataset.kyndLabel != null) {
      activeBtn.textContent = activeBtn.dataset.kyndLabel;
      delete activeBtn.dataset.kyndLabel;
    }
  }

  function clearError(card) {
    if (!card) return;
    var err = card.querySelector('.kynd-upsell__error');
    if (err) err.textContent = '';
  }

  function showError(card, message) {
    if (!card) return;
    var err = card.querySelector('.kynd-upsell__error');
    if (err) err.textContent = message || 'Something went wrong. Please try again.';
  }

  // --- Rapi paid-scraper-upsell resolution -----------------------------
  // The "rapi" bundle app (the box-quantity-break bundle attached to the
  // toothbrush) embeds its full merchant config as JSON inside a page
  // <script> tag on every page — confirmed live: the identical blob is
  // present on both a product page and the homepage, so it's available
  // wherever the cart drawer can open, not just on a product page. That
  // same embedded JSON is what its own Shopify discount Function keys its
  // "offer match" on: confirmed by capturing real Rapi network requests
  // (DevTools) for the 1-box and 2-box paid-scraper adds and finding their
  // exact `offer`/`upid` tokens present verbatim in this same config.
  // Reading it here — instead of hardcoding a market/quantity -> offer
  // table — means this keeps working if the merchant edits prices, adds a
  // market, or Rapi reassigns ids, which a hardcoded table would silently
  // go stale against.
  var rapiQuantityBreakBundlesCache = null;

  // A JSON-escaped string literal's raw (still-escaped) content, starting
  // right after its opening quote — walks to the matching closing quote by
  // counting backslash-escapes, so it doesn't need a real JSON parser to
  // find where an embedded string value ends before parsing it.
  function extractJsonStringLiteral(text, start) {
    var j = start;
    while (true) {
      j = text.indexOf('"', j);
      if (j === -1) return null;
      var backslashes = 0;
      var k = j - 1;
      while (k >= 0 && text.charAt(k) === '\\') { backslashes++; k--; }
      if (backslashes % 2 === 0) break;
      j++;
    }
    return text.slice(start, j);
  }

  function rapiQuantityBreakBundles() {
    // Only ever trust and cache a NON-EMPTY scan. A `[]` result is
    // indistinguishable, from in here, between "this page genuinely has no
    // Rapi bundle config" and "the scan ran before Rapi's embedded config
    // script tag was actually present/parsed" — and `[]` is truthy in JS,
    // so caching it unconditionally (the previous behavior) would permanently
    // poison EVERY dependent resolver (main offer, scraper upsell, gift) for
    // the rest of the page's lifetime the moment any single call happened to
    // run at an unlucky instant, with no way to ever recover without a full
    // reload. Re-scanning is cheap (a synchronous, in-memory DOM read, not a
    // network call), so there is no cost to only committing the cache once a
    // real result is found, and retrying on every call until then.
    if (rapiQuantityBreakBundlesCache && rapiQuantityBreakBundlesCache.length) return rapiQuantityBreakBundlesCache;
    var bundles = [];
    var scripts = document.getElementsByTagName('script');
    var re = /"bundle_\d+":"/g;
    for (var i = 0; i < scripts.length; i++) {
      var text = scripts[i].textContent;
      if (!text || text.indexOf('"bundle_') === -1) continue;
      re.lastIndex = 0;
      var m;
      while ((m = re.exec(text))) {
        var raw = extractJsonStringLiteral(text, m.index + m[0].length);
        if (raw === null) continue;
        try {
          var obj = JSON.parse(JSON.parse('"' + raw + '"'));
          if (obj && obj.bundle_type === 'quantity_breaks') bundles.push(obj);
        } catch (e) { /* not our config shape — skip */ }
      }
    }
    if (bundles.length) rapiQuantityBreakBundlesCache = bundles;
    return bundles;
  }

  function rapiMarketCode() {
    var el = document.getElementById('rappi-country-code');
    return el ? String(el.value || '').toUpperCase() : '';
  }

  // Rapi runs one SEPARATE quantity-break bundle record per target market
  // (confirmed live: a US-only bundle and an EU-market bundle both exist
  // for this same toothbrush, sharing one discount_app_id but with
  // DIFFERENT offer/upsell identifier tokens each). Matching the wrong one
  // for the visitor's market would tag the scraper line with an offer id
  // Shopify's discount Function doesn't recognize for their actual market.
  // This mirrors Rapi's own `_isBundleVisibleInMarket` check (present in
  // their shipped script) exactly: no target_markets set = visible in every
  // market; otherwise the visitor's resolved market code must be listed.
  function bundleVisibleInMarket(bundle, marketCode) {
    var markets = bundle.target_markets;
    if (!markets || !Array.isArray(markets) || markets.length === 0) return true;
    for (var i = 0; i < markets.length; i++) {
      if (String(markets[i]).toUpperCase() === marketCode) return true;
    }
    return false;
  }

  // A SECOND, independent axis of duplication found live: Rapi also keeps
  // one SEPARATE "BUNDLE & SAVE" quantity_breaks bundle per SHOPIFY PRODUCT
  // — e.g. one for the real toothbrush and, per market, up to four more for
  // the "test" staging product (GB / a multi-country group / the ~27-country
  // EU group / US) — and at least one of those product-scoped duplicates
  // shares the EU one's exact tier identifiers (eJJ7h/ATgUe/1XW29) while
  // targeting a DIFFERENT product with NO market restriction at all. A
  // resolver keyed on market alone can silently match the wrong product's
  // bundle when more than one candidate is visible for the same market —
  // confirmed live via a Node simulation using the real embedded config:
  // without this check, a 2-box lookup for the "test" product's own EU
  // bundle (185066) instead matched an unrelated, market-unrestricted
  // bundle (141388) that happened to load earlier in page order. Each
  // bundle's own `selected_products` array is the only reliable signal for
  // which product it actually belongs to, so it's required, not optional:
  // a bundle with no (or unparseable) product scope never matches anything,
  // rather than being treated as "matches every product".
  function bundleAppliesToProduct(bundle, productId) {
    if (!productId) return false;
    var selected = bundle.selected_products;
    if (!Array.isArray(selected) || selected.length === 0) return false;
    for (var i = 0; i < selected.length; i++) {
      if (Number(selected[i].shopify_product_id) === Number(productId)) return true;
    }
    return false;
  }

  // Shared lookup: for the given box quantity and the SPECIFIC toothbrush
  // Shopify product id actually in the cart line being reconciled, finds
  // the matching Rapi offer tier in whichever bundle both (a) is scoped to
  // that exact product and (b) targets the visitor's market. Every
  // offer-quantity resolver below is built on this ONE scan, so the
  // main-offer and scraper-upsell resolvers can never disagree about which
  // bundle/offer is authoritative. Returns the raw offer object (plus its
  // owning bundleId) or null; callers never invent a match when this
  // returns null.
  function resolveRapiOfferForQuantity(boxQty, productId) {
    var marketCode = rapiMarketCode();
    var bundles = rapiQuantityBreakBundles();
    for (var i = 0; i < bundles.length; i++) {
      if (!bundleAppliesToProduct(bundles[i], productId)) continue;
      if (!bundleVisibleInMarket(bundles[i], marketCode)) continue;
      var offers = bundles[i].offers || [];
      for (var j = 0; j < offers.length; j++) {
        if (Number(offers[j].quantity) === Number(boxQty)) {
          return { bundleId: bundles[i].id, offer: offers[j] };
        }
      }
    }
    return null;
  }

  // The toothbrush's own MAIN offer for a given box quantity/product —
  // exists for every tier, including 3 boxes (which has no paid scraper
  // upsell, but still has its own main offer identifier).
  // discountType/discountValue are the offer's OWN raw Rapi fields
  // (confirmed live: "default"/0 at 1 box, "percentage"/10 at 2,
  // "percentage"/15 at 3) — additive on this return object only, never
  // read by rapiMainOfferProperties/foldMainOfferIntoChangeBody (which
  // pick bundleId/offerIdentifier explicitly), so this cannot change what
  // gets written to a cart line's properties. Used only by the display
  // pricing helpers below.
  function resolveRapiMainOffer(boxQty, productId) {
    var found = resolveRapiOfferForQuantity(boxQty, productId);
    return found ? {
      bundleId: found.bundleId,
      offerIdentifier: found.offer.identifier_id,
      discountType: found.offer.discount_type,
      discountValue: Number(found.offer.discount_value)
    } : null;
  }

  // The paid-scraper upsell nested inside that same tier's offer (a tier
  // may have none — the 3-box tier doesn't, since the scraper becomes the
  // free-gift state there instead). Returns null when no match exists;
  // callers must never invent one. discountType/discountValue are the raw
  // Rapi fields (confirmed live: discount_type "specific" at every tier —
  // a FIXED final upsell price, not an amount-off — with discount_value
  // 14.95/14.95/9.95) — additive only, never read by
  // rapiScraperUpsellProperties (which picks bundleId/offerIdentifier/
  // upsellIdentifier explicitly), so this cannot change what gets written
  // to a cart line's properties.
  function resolveRapiScraperUpsell(boxQty, productId, scraperProductId) {
    var found = resolveRapiOfferForQuantity(boxQty, productId);
    if (!found) return null;
    var upsells = found.offer.offer_upsells || [];
    for (var k = 0; k < upsells.length; k++) {
      if (Number(upsells[k].shopify_product_id) === Number(scraperProductId)) {
        return {
          bundleId: found.bundleId,
          offerIdentifier: found.offer.identifier_id,
          upsellIdentifier: upsells[k].identifier_id,
          discountType: upsells[k].discount_type,
          discountValue: Number(upsells[k].discount_value)
        };
      }
    }
    return null;
  }

  // ALL scraper upsells configured for this tier (not just the first
  // product-id match) — confirmed live that Rapi now configures TWO
  // distinct scraper upsells on the 1- and 2-box offers (same scraper
  // product, different identifier_id/discount_value each — position 1 =
  // EUR14.95, position 2 = EUR9.95), sorted by Rapi's own `position`
  // field exactly as its own widget renders them. The 3-box tier still
  // has exactly one, so this returns a single-item array there — nothing
  // about that call path changes shape or count.
  function resolveRapiScraperUpsellsForQuantity(boxQty, productId, scraperProductId) {
    var found = resolveRapiOfferForQuantity(boxQty, productId);
    if (!found) return [];
    var upsells = (found.offer.offer_upsells || []).filter(function (u) {
      return Number(u.shopify_product_id) === Number(scraperProductId);
    });
    upsells.sort(function (a, b) { return (a.position || 0) - (b.position || 0); });
    return upsells.map(function (u) {
      return {
        bundleId: found.bundleId,
        offerIdentifier: found.offer.identifier_id,
        upsellIdentifier: u.identifier_id,
        discountType: u.discount_type,
        discountValue: Number(u.discount_value)
      };
    });
  }

  // Generalization of resolveRapiScraperUpsellsForQuantity above, for the
  // OPPOSITE direction: the scraper's own PDP now publishes a toothbrush
  // cross-sell on its 1- and 2-scraper offers (none on the 4-scraper FULL
  // HOUSE tier), and unlike the toothbrush side this caller doesn't already
  // know which product id to filter for — there's no toothbrush line in
  // the cart yet to read one off of. Returns EVERY upsell configured on the
  // given product's offer for this quantity, each carrying its own
  // targetProductId read straight from Rapi's config, so a caller can
  // discover the target product instead of assuming it. Never invents a
  // product id: an upsell with no product mismatch simply means the
  // caller can't act on it, not that a default should be guessed.
  function resolveRapiOfferUpsells(qty, productId) {
    var found = resolveRapiOfferForQuantity(qty, productId);
    if (!found) return [];
    var upsells = (found.offer.offer_upsells || []).slice();
    upsells.sort(function (a, b) { return (a.position || 0) - (b.position || 0); });
    return upsells.map(function (u) {
      return {
        bundleId: found.bundleId,
        offerIdentifier: found.offer.identifier_id,
        upsellIdentifier: u.identifier_id,
        discountType: u.discount_type,
        discountValue: Number(u.discount_value),
        targetProductId: Number(u.shopify_product_id)
      };
    });
  }

  // The toothbrush cross-sell CURRENTLY configured on the scraper's own
  // offer for the given scraper quantity — resolved fresh every call,
  // exactly like every other Rapi lookup in this file. Returns null
  // (never a guess) when Rapi has no upsell configured there at all — the
  // published FULL HOUSE tier (qty 4) is exactly this case, so the
  // scraper-first cross-sell offer correctly disappears there with no
  // quantity-specific special-casing.
  function resolveScraperCrossSellUpsell(scraperQty, c) {
    return resolveRapiOfferUpsells(scraperQty, c.scraperProductId)[0] || null;
  }

  // The real reduction in the scraper's OWN payable price that the
  // scraper-first -> toothbrush handoff would cause, for the "Add
  // toothbrushes" cross-sell's single-scraper copy: resolves the toothbrush
  // tier's own FIRST configured paid-scraper slot (the position the lone
  // existing scraper would land on — see reconcileScraperOffer's own
  // fan-out) and reuses rapiUpsellDisplayPricing's own savingsCents,
  // computed against the SAME live scraperPriceCents reference every other
  // scraper price in this file already uses. Returns null (never a guess)
  // whenever the cross-sell, the destination slot, or its pricing can't be
  // cleanly resolved — callers must fail open to static copy in that case.
  function resolveScraperHandoffSavingCents(c) {
    var crossSell = resolveScraperCrossSellUpsell(1, c);
    if (!crossSell) return null;
    var destinationUpsells = resolveRapiScraperUpsellsForQuantity(1, crossSell.targetProductId, c.scraperProductId);
    var firstSlot = destinationUpsells[0];
    if (!firstSlot) return null;
    var pricing = rapiUpsellDisplayPricing(firstSlot, c);
    return pricing ? pricing.savingsCents : null;
  }

  // The scraper upsell to OFFER next: the first of this tier's configured
  // upsells (in Rapi's own position order) whose upsellIdentifier isn't
  // already represented by a real paid scraper line in the cart. Never
  // decides "first vs second" from the Shopify product id (both upsells
  // share the same scraper product) — only from each line's own real
  // __rapi_offer.upid, read back from the cart exactly like every other
  // stale-tag check in this file. Returns null once every configured
  // upsell for this tier already has its own line (the "no more scraper
  // offers" state) or when nothing is configured at all — never invents a
  // next tier. For the 3-box tier (exactly one configured upsell) this is
  // equivalent to the existing !findPaidScraperLine(cart,c) check it
  // doesn't replace (that gate is left untouched — see reconcileThreeBoxGift).
  function resolveNextRapiScraperUpsell(cart, c, boxQty) {
    var productId = primaryToothbrushProductId(cart, c);
    if (!productId) return null;
    var candidates = resolveRapiScraperUpsellsForQuantity(boxQty, productId, c.scraperProductId);
    if (!candidates.length) return null;
    var usedUpids = findPaidScraperLines(cart, c).map(function (line) {
      var raw = line.properties && line.properties.__rapi_offer;
      try {
        var parsed = JSON.parse(raw);
        return parsed && parsed.upid;
      } catch (e) {
        return null;
      }
    }).filter(Boolean);
    for (var i = 0; i < candidates.length; i++) {
      if (usedUpids.indexOf(candidates[i].upsellIdentifier) === -1) return candidates[i];
    }
    return null;
  }

  // Which POSITION (0-based, Rapi's own position order) a paid scraper
  // line's CURRENT upid corresponds to — checked against every 1/2-box
  // tier's own configured upsells (never the 3-box tier, whose own single
  // upsell/coexistence rule is reconcileThreeBoxGift's separate, untouched
  // concern), since a line's stale upid was assigned under whichever box
  // quantity it was originally added at, not necessarily the current one.
  // Returns null when the upid isn't recognized by either tier's current
  // config at all (e.g. a since-reconfigured/removed upsell) — callers
  // must never guess a position in that case.
  function rapiScraperUpsellPositionForUpid(productId, scraperProductId, upid) {
    if (!upid) return null;
    for (var q = 1; q <= 2; q++) {
      var candidates = resolveRapiScraperUpsellsForQuantity(q, productId, scraperProductId);
      for (var i = 0; i < candidates.length; i++) {
        if (candidates[i].upsellIdentifier === upid) return i;
      }
    }
    return null;
  }

  // The Shopify product id of whichever allowlisted toothbrush line is
  // actually in the cart — cart line items always carry their own
  // `product_id` (standard Shopify Cart API field), so this needs no new
  // Liquid data attribute and stays correct for BOTH family members (the
  // real toothbrush and the `test` staging product each have their OWN
  // separate Rapi bundle — see bundleAppliesToProduct above). If more than
  // one family member is in the cart at once (mixing real + test — not a
  // real shopper scenario, only possible during QA), the first matching
  // line's product is used; genuinely resolving a MIXED-family cart against
  // two different bundles at once isn't a case any single call can answer,
  // so this doesn't attempt to.
  function primaryToothbrushProductId(cart, c) {
    var line = cart.items.find(function (l) { return matchesVariant(l, brushIds(c)); });
    return line ? line.product_id : null;
  }

  // The `id` inside __rapi_offer (a stable per-session token Rapi assigns,
  // confirmed identical across every one of the user's captured requests
  // in a session) isn't present anywhere in the embedded bundle config —
  // but Rapi's OWN toothbrush add already stamped it onto the toothbrush
  // line's own __rapi_offer property, and Shopify's Cart API always echoes
  // line properties back verbatim in every /cart.js response. So instead
  // of deriving it, read it off the cart we already have.
  //
  // Deliberately reads ONLY `.id` — never `.offer` — from the toothbrush
  // line: that line's `offer` reflects whatever tier it was ORIGINALLY
  // added under and goes stale the moment our own quantity CTAs change box
  // count via a plain /cart/change.js (which never touches properties).
  // The current tier's real offer/upid always comes fresh from
  // resolveRapiScraperUpsell() below, never from this line.
  function findRapiMainOfferId(cart, c) {
    var lines = cart.items.filter(function (line) { return matchesVariant(line, brushIds(c)); });
    for (var i = 0; i < lines.length; i++) {
      var raw = lines[i].properties && lines[i].properties.__rapi_offer;
      if (!raw) continue;
      try {
        var parsed = JSON.parse(raw);
        if (parsed && parsed.id != null && String(parsed.id) !== '') return String(parsed.id);
      } catch (e) { /* unparseable — try the next matching line, if any */ }
    }
    return null;
  }

  // Generalization of findRapiMainOfferId above for the scraper-first
  // handoff: the SAME stable per-session id is stamped on every Rapi-tagged
  // line regardless of which product/bundle it belongs to (confirmed live
  // across this whole engagement's captures), so when adding the very
  // FIRST toothbrush line — one that has no id of its own yet — the
  // correct id to reuse is already sitting on the existing scraper line(s)
  // Rapi's own PDP widget added. Reads ANY __rapi_offer.id off a line
  // matching the given variant, regardless of shape (main or upsell) —
  // never invents one.
  function findAnyRapiSessionId(cart, variantId) {
    var lines = cart.items.filter(function (line) { return line.variant_id === variantId; });
    for (var i = 0; i < lines.length; i++) {
      var raw = lines[i].properties && lines[i].properties.__rapi_offer;
      if (!raw) continue;
      try {
        var parsed = JSON.parse(raw);
        if (parsed && parsed.id != null && String(parsed.id) !== '') return String(parsed.id);
      } catch (e) { /* unparseable — try the next matching line, if any */ }
    }
    return null;
  }

  // Reproduces the property shape of Rapi's own paid-scraper-upsell add,
  // captured from real network requests. `mainId` must come from
  // findRapiMainOfferId() (the toothbrush line's own stamped id) —
  // never invented, never hardcoded.
  function rapiScraperUpsellProperties(match, mainId) {
    return {
      __rapi_bundles: String(match.bundleId),
      __rapi_offer: JSON.stringify({ id: mainId, offer: match.offerIdentifier, upsell: true, upid: match.upsellIdentifier }),
      __rapi_market: rapiMarketCode()
    };
  }

  // Reproduces the property shape of Rapi's own MAIN toothbrush-line add —
  // captured live. IMPORTANT: this is a DIFFERENT key shape than the
  // scraper upsell line above — confirmed from real captures of both line
  // types in the same cart: the main line uses single-underscore
  // `_rapi_bundles` / `_rapi_market` (no `_market`/`_bundles` doubling) and
  // an `__rapi_offer` shaped `{main:true, id, offer}` (no upsell/upid).
  // Never "normalize" these to match the scraper line's double-underscore
  // keys — they are genuinely different per Rapi's own native add.
  //
  // `existingProperties` is cloned and only the three Rapi keys are
  // overwritten, so any unrelated property already on the line (gift tags
  // don't apply to a main line, but nothing here assumes that) survives
  // untouched — required because /cart/change.js REPLACES a line's whole
  // properties object with whatever is sent, it does not merge.
  function rapiMainOfferProperties(existingProperties, match, mainId) {
    var properties = {};
    for (var key in existingProperties) {
      if (Object.prototype.hasOwnProperty.call(existingProperties, key)) properties[key] = existingProperties[key];
    }
    properties.__rapi_offer = JSON.stringify({ main: true, id: mainId, offer: match.offerIdentifier });
    properties._rapi_bundles = String(match.bundleId);
    properties._rapi_market = rapiMarketCode();
    return properties;
  }

  // Keeps the toothbrush line's OWN Rapi main-offer metadata matched to its
  // CURRENT box quantity. Confirmed live: our quantity CTAs (and the native
  // stepper) change box count via a plain /cart/change.js that only touches
  // quantity, never properties — so the line keeps quoting whatever tier it
  // was ORIGINALLY added under. The toothbrush's own box-tier PRICE is
  // unaffected (that discount is quantity-based, not property-gated — the
  // live 2-box capture priced correctly at 53.91 even while still tagged
  // eJJ7h) but Rapi's scraper-upsell discount Function cross-checks the
  // scraper line's offer against a "main" line that actually carries that
  // SAME offer id, so a stale main-line tag silently breaks the SCRAPER's
  // discount even though the toothbrush's own price looks fine. This must
  // run, and settle, before any scraper reconciliation.
  //
  // In place update (never remove/re-add): /cart/change.js can set
  // properties without touching quantity or selling_plan when neither is
  // included as a change — the safest way to retag a subscription line
  // without disturbing its plan.
  function reconcileMainOffer(cart, c) {
    if (reconciling) return Promise.resolve(cart);
    var boxQty = sumQty(cart, brushIds(c));
    if (boxQty < 1) return Promise.resolve(cart);

    var lines = cart.items.filter(function (line) { return matchesVariant(line, brushIds(c)); });
    for (var i = 0; i < lines.length; i++) {
      var raw = lines[i].properties && lines[i].properties.__rapi_offer;
      if (!raw) continue; // never added via Rapi's own flow — nothing of ours to reconcile
      var current;
      try { current = JSON.parse(raw); } catch (e) { continue; }
      if (!current || current.offer == null) continue;
      if (current.id == null || String(current.id) === '') continue; // never invent the session id
      // Resolved per-line, against THAT line's own product — the real
      // toothbrush and the `test` staging product each have their own
      // separate Rapi bundle (see bundleAppliesToProduct).
      var target = resolveRapiMainOffer(boxQty, lines[i].product_id);
      if (!target || current.offer === target.offerIdentifier) continue;

      reconciling = true;
      // FIXED (live bug): `quantity` used to be deliberately omitted here on
      // the theory that /cart/change.js leaves quantity untouched when it's
      // absent from the body. Confirmed live against the real Cart API that
      // this is false — omitting `quantity` resets the line to 1, it does
      // NOT preserve the existing quantity. That silently collapsed real
      // cart lines to 1 on every properties-only retag. `quantity` must
      // always be included explicitly; `lines[i].quantity` is read from the
      // same fresh cart snapshot this reconciliation pass already fetched,
      // so it reflects the real current quantity at the time of this call.
      // The response is already the fresh, authoritative cart — no need
      // for a separate GET just to re-read what this same response has.
      return mutateCart(routes().change, {
        id: lines[i].key,
        quantity: lines[i].quantity,
        properties: rapiMainOfferProperties(lines[i].properties, target, String(current.id))
      })
        .finally(function () {
          reconciling = false;
        });
    }
    return Promise.resolve(cart);
  }

  // EVERY scraper line that isn't the free-gift line (see findGiftScraperLine,
  // which recognizes BOTH gift shapes) AND that is positively identifiable as
  // belonging to Rapi's paid-upsell mechanism — i.e. added through the paid
  // Rapi upsell path, whatever tier/upsell each currently carries. Now that a
  // tier can configure more than one scraper upsell (confirmed live: 1-
  // and 2-box tiers each now have two), more than one such line can be
  // genuinely, simultaneously correct — see resolveNextRapiScraperUpsell.
  //
  // FIXED (live bug, two rounds): round 1 excluded plain, manually-added
  // scraper lines (zero properties) from this filter — but that wasn't
  // sufficient. Round 2: the scraper PDP's own Rapi quantity-break bundle
  // (e.g. "Full House", 4 units, 20% off — a completely normal, customer-
  // selected purchase) tags its line with __rapi_offer too, shaped
  // {main:true, id, offer} — the SAME main-line shape rapiMainOfferProperties
  // uses for the toothbrush's own line. Bare "__rapi_offer exists" therefore
  // is NOT sufficient to mean "ours": confirmed against live cart JSON that
  // a normal scraper-bundle line and a genuine toothbrush-tier upsell line
  // both carry __rapi_offer, and only the latter has upsell:true/upid.
  // isRapiScraperUpsellProperties checks for that exact shape — only a line
  // positively identified as the toothbrush's own upsell may be claimed,
  // converted, retagged, capped, or removed by this reconciliation. A normal
  // customer-added OR scraper-bundle-tagged line is never "ours" to touch,
  // regardless of what __rapi_offer it carries.
  function findPaidScraperLines(cart, c) {
    return cart.items.filter(function (line) {
      if (line.variant_id !== c.scraperVariantId) return false;
      if (line.properties && line.properties[GIFT_PROP_KEY] === GIFT_PROP_VALUE) return false;
      if (isRapiNativeGiftProperties(line.properties)) return false;
      return isRapiScraperUpsellProperties(line.properties);
    });
  }

  // The first paid scraper line, for the callers that only ever need to
  // know "does at least one exist" (the 3-box tier, which only ever
  // configures one upsell, so "first" and "only" are the same line there —
  // see reconcileThreeBoxGift/getState, both left unmodified).
  function findPaidScraperLine(cart, c) {
    return findPaidScraperLines(cart, c)[0];
  }

  // Locates the ONE paid scraper line tagged with a SPECIFIC upid — needed
  // wherever a particular upsell tier's own line must be confirmed/
  // reconciled now that more than one paid scraper line can coexist
  // (findPaidScraperLine's "first match" is no longer precise enough for
  // settlement-confirmation of a JUST-added line when another, already-
  // settled paid line already exists).
  function findPaidScraperLineByUpid(cart, c, upid) {
    if (!upid) return null;
    return findPaidScraperLines(cart, c).find(function (line) {
      var raw = line.properties && line.properties.__rapi_offer;
      try {
        var parsed = JSON.parse(raw);
        return parsed && parsed.upid === upid;
      } catch (e) {
        return false;
      }
    }) || null;
  }

  // Generic "got some real discount" signal for the PAID 1/2-box upsell —
  // any genuine reduction counts, since the exact target price is never
  // hardcoded here.
  function isDiscountedScraperLine(line) {
    return !!line && line.original_line_price > 0 && line.final_line_price < line.original_line_price;
  }

  // Strict "this is the authoritative free-gift state" signal for the
  // 3-box gift — deliberately NOT just isDiscountedScraperLine(), since a
  // merely PARTIALLY discounted scraper (e.g. a stale paid-upsell price)
  // would also satisfy final < original. Requires all three: the line is
  // genuinely free, it carries the Rapi-native gift shape we tagged it
  // with, and Shopify's own response shows the real "3 Boxes" 100%
  // allocation actually applied. Shopify can omit its allocation type;
  // the full gift allocation must still independently cover the original.
  function isSettledFreeGiftLine(line) {
    if (!line || line.final_line_price !== 0 || !(line.quantity > 0) || !(line.original_line_price > 0) ||
        line.selling_plan_allocation || !isRapiNativeGiftProperties(line.properties)) return false;
    return (line.line_level_discount_allocations || []).some(function (allocation) {
      var discount = allocation.discount_application || {};
      return allocation.amount === line.original_line_price &&
        discount.title === '3 Boxes' && discount.value_type === 'percentage' && Number(discount.value) === 100 &&
        discount.target_type === 'line_item' &&
        (discount.type === 'automatic' || discount.type === '' || discount.type == null);
    });
  }

  // Confirmed live: right after adding/retagging a scraper line (paid OR
  // gift), the FIRST /cart.js read can still show it unsettled — Rapi's
  // discount Function hasn't finished yet — with the correct state (a paid
  // tier's real price, or the gift's confirmed 100%-off) only appearing on
  // a later read. Poll a few real /cart.js reads with a short delay
  // between them rather than show (or let a caller trust) that transient
  // state. Bounded (never indefinite): once `attemptsLeft` runs out it
  // just returns whatever the last real read was — this never invents a
  // price or assumes success itself; callers decide what "never settled"
  // means for them. `findLine` locates whichever specific line the caller
  // just mutated; `isConfirmed` is the caller-specific settlement check
  // (isDiscountedScraperLine for paid, isSettledFreeGiftLine for the gift
  // — never shared, so a partially-discounted line can never pass as a
  // confirmed free gift).
  //
  // This ALSO forces the NATIVE cart-drawer sections to catch up: a plain
  // fetchCart() read never re-renders them, so without this nudge the
  // drawer can keep showing whatever the original add/change response
  // contained — often still full retail, exactly the flicker this exists
  // to close. The nudge fires exactly once, on whichever read is the LAST
  // one this call makes — either the read that first confirms settlement,
  // or (if the wait budget runs out first) the final unconfirmed read —
  // never zero times (the drawer would stay stale forever) and never more
  // than once (that would just be a second, redundant render). It never
  // invents a price either way: on the confirmed path it shows the real
  // settled state; on the exhausted path it shows whatever the real
  // last-known state genuinely is, truthfully, not a guess. Scoped to this
  // one line's own (unchanged) quantity via /cart/change.js — it can never
  // touch cart-level discount codes/attributes (BEKYND stacking), and is
  // marked as our own mutation so the global fetch wrap never treats it as
  // an external change and fires a redundant refresh() from it.
  function waitForScraperSettlement(c, attemptsLeft, findLine, isConfirmed) {
    return fetchCart().then(function (cart) {
      var line = findLine(cart, c);
      var confirmed = isConfirmed(line);
      if (confirmed || attemptsLeft <= 0) {
        if (!line) return cart; // nothing left to nudge (e.g. a failed add produced no line at all)
        return mutateCart(routes().change, { id: line.key, quantity: line.quantity })
          .then(function () { return cart; });
      }
      return new Promise(function (resolve) { setTimeout(resolve, 500); })
        .then(function () { return waitForScraperSettlement(c, attemptsLeft - 1, findLine, isConfirmed); });
    });
  }

  // Checks a mutation's OWN response BEFORE falling back to a separate
  // /cart.js poll. Per the vendor's confirmed contract this is very likely
  // to already prove settlement for a property CHANGE to an EXISTING line;
  // for a genuinely fresh /cart/add.js line this has been independently
  // observed live to sometimes still need a beat, so this never ASSUMES
  // success from a fresh add — it only ever trusts what isConfirmed()
  // actually finds true, on this exact response, right now. When it can't
  // prove it yet, falls back to the EXACT SAME bounded poll+nudge as
  // before (waitForScraperSettlement, unmodified) — zero correctness
  // change, only removes the redundant read when the answer was already
  // sitting in a response we already have.
  function confirmScraperMutation(mutateResponse, c, findLine, isConfirmed) {
    var line = mutateResponse && Array.isArray(mutateResponse.items) ? findLine(mutateResponse, c) : null;
    if (isConfirmed(line)) {
      // Still exactly one unsuppressed nudge — the add/retag itself was
      // suppressed, so this remains the one real render, same as the
      // confirmed branch inside waitForScraperSettlement.
      return mutateCart(routes().change, { id: line.key, quantity: line.quantity })
        .then(function () { return mutateResponse; });
    }
    return waitForScraperSettlement(c, 3, findLine, isConfirmed);
  }

  function handleAddScraper(c, card, btn) {
    clearError(card);
    setBusy(card, true, btn, 'Adding…');
    // Captured from the initial fetchCart() read and reused for
    // settlement-confirmation below — now that a tier can have more than
    // one paid scraper line coexisting, confirmation must target THIS
    // specific upsell's own upid (findPaidScraperLineByUpid), never
    // "whichever paid line comes first" (findPaidScraperLine), which could
    // silently confirm an unrelated, already-settled line instead of the
    // one this call just added.
    var pendingMatch = null;
    fetchCart()
      .then(function (cart) {
        var boxQty = sumQty(cart, brushIds(c));
        var mainId = findRapiMainOfferId(cart, c);
        var match = resolveNextRapiScraperUpsell(cart, c, boxQty);
        pendingMatch = match;
        // Fail closed: never add the scraper at all if either half of the
        // real Rapi state can't be confirmed — an untagged add would
        // silently bypass the discount while the card still advertises a
        // "special cart price".
        if (!mainId || !match) throw new Error('Could not verify the current scraper offer.');
        // Suppressed: a fresh /cart/add.js line's own response has been
        // independently observed live to sometimes still show full retail
        // for one beat before Rapi's Function settles, so painting it would
        // show the customer a full-price scraper that then flips to
        // discounted — the exact flicker waitForScraperSettlement's own
        // nudge exists to replace with a single, correct render instead.
        return mutateCart(routes().add, {
          items: [{ id: c.scraperVariantId, quantity: 1, properties: rapiScraperUpsellProperties(match, mainId) }]
        }, true);
      })
      .then(function (addResponse) {
        return confirmScraperMutation(addResponse, c, function (cartArg, cfgArg) {
          return findPaidScraperLineByUpid(cartArg, cfgArg, pendingMatch.upsellIdentifier);
        }, isDiscountedScraperLine);
      })
      .then(function (cart) {
        var line = findPaidScraperLineByUpid(cart, c, pendingMatch.upsellIdentifier);
        var discounted = !!line && line.original_line_price > 0 && line.final_line_price < line.original_line_price;
        if (discounted) return cart;
        // The real discount never materialized within the wait budget —
        // fail closed: remove the undiscounted line rather than leave a
        // full-price scraper sitting in the cart under a "special cart
        // price" card.
        if (!line) throw new Error('Could not confirm the scraper discount.');
        return mutateCart(routes().change, { id: line.key, quantity: 0 }).then(function () {
          throw new Error('Could not confirm the scraper discount.');
        });
      })
      .then(refresh)
      .catch(function (e) {
        showError(card, 'Could not add the scraper. Please try again.');
        console.error(e);
      })
      .finally(function () {
        setBusy(card, false, btn);
      });
  }

  // Reproduces the property shape of Rapi's own NATIVE free-gift scraper
  // line — captured live: {id, offer, gift:true} (no upsell/upid — a
  // THIRD, distinct shape from both the main line's and the paid-upsell
  // line's), same single-underscore _rapi_bundles/_rapi_market as the main
  // line. `match` is the 3-box MAIN offer (resolveRapiMainOffer), not a
  // scraper-upsell match — the 3-box tier defines no paid upsell, only
  // this gift, and the gift's id/offer are the SAME as the main line's.
  function rapiGiftScraperProperties(match, mainId) {
    return {
      __rapi_offer: JSON.stringify({ id: mainId, offer: match.offerIdentifier, gift: true }),
      _rapi_bundles: String(match.bundleId),
      _rapi_market: rapiMarketCode()
    };
  }

  // Keeps EVERY existing PAID scraper line's Rapi offer metadata matched
  // to the toothbrush's CURRENT box quantity/tier (1 or 2 boxes only — see
  // reconcileThreeBoxGift for 3+). The scraper line's own properties are
  // what Rapi's discount Function actually reads — nothing updates them
  // automatically just because the toothbrush quantity changed elsewhere
  // in the cart, so a line added under the 1-box offer keeps quoting that
  // offer (and Rapi correctly stops discounting it) after an upgrade to 2
  // boxes. Confirmed live.
  //
  // Now that a tier can configure more than one scraper upsell (live: 1-
  // and 2-box tiers each have two), a line's stale upid no longer tells us
  // WHICH destination upsell it should retag to — only that it belongs to
  // whichever POSITION it currently occupies (rapiScraperUpsellPositionForUpid),
  // carried over unchanged: the line that was the FIRST (EUR14.95) upsell
  // stays the first upsell at the new tier, the SECOND stays second.
  //
  // FIXED (live bug): this used to fix only the FIRST stale line per call,
  // relying on "the next refresh() pass" to catch a second one — but
  // nothing actually triggers a second refresh() pass for a single
  // customer action, so when BOTH the 1-box tier's paid lines were stale
  // at once (a genuinely new possibility now that a tier can have two),
  // only one ever got retagged; the other kept quoting its old tier's upid
  // forever, which Rapi's Function then stopped discounting (full retail),
  // while resolveNextRapiScraperUpsell — seeing that stale upid didn't
  // match either destination-tier candidate — wrongly reported the
  // position it belonged to as "not yet taken" and re-offered it as a CTA.
  //
  // Fix: snapshot EVERY paid line's source position and destination target
  // up front, from the cart exactly as it is NOW, before touching any of
  // them — so retagging one line can never contaminate another's
  // source-position inference — then run every genuinely stale retag as
  // one sequential chain, so refresh() only calls render()/getState() once
  // every stale line has been retagged AND had its own settlement
  // confirmed. Still per-line remove+add (never a bulk delete/re-add),
  // exactly the existing single-line mutation shape, just run once per
  // stale line instead of once total.
  function reconcileScraperOffer(cart, c) {
    if (reconciling) return Promise.resolve(cart);
    var boxQty = sumQty(cart, brushIds(c));

    // Reverse of the forward handoff below: once the LAST toothbrush is
    // gone, any remaining paid scraper line has no toothbrush tier left to
    // belong to at all — hand ownership back to the scraper's own bundle.
    if (boxQty === 0) return reconcileScraperOwnershipReversion(cart, c);
    if (boxQty >= 3) return reconcileThreeBoxGift(cart, c, boxQty);
    if (boxQty !== 1 && boxQty !== 2) return Promise.resolve(cart);

    var lines = findPaidScraperLines(cart, c);
    if (!lines.length) return Promise.resolve(cart);

    var productId = primaryToothbrushProductId(cart, c);
    if (!productId) return Promise.resolve(cart);
    var candidates = resolveRapiScraperUpsellsForQuantity(boxQty, productId, c.scraperProductId);
    if (!candidates.length) return Promise.resolve(cart); // no destination to reconcile against — leave every line alone

    var mainId = findRapiMainOfferId(cart, c);
    if (!mainId) return Promise.resolve(cart); // can't recreate correctly without the real toothbrush id — never guess it

    // --- Plan: read every line's CURRENT (pre-mutation) position/staleness
    // in one pass over the untouched snapshot. Nothing below this point
    // re-derives a position from the cart — each planned step already
    // carries its own fixed line key/targets.
    //
    // Lines split into two groups: RECOGNIZED (their current upid already
    // belongs to one of this tier's own slots — the pre-existing tier-
    // transition case, e.g. 1-box -> 2-box) each keep their own resolved
    // position untouched, exactly as before. UNRECOGNIZED lines (a foreign
    // upid — e.g. carried over from the SCRAPER-PDP's own bundle via the
    // scraper-first handoff, or no upid at all) fan out ACROSS the
    // remaining unclaimed slots in cart order, one unit per slot — a
    // single scraper-bundle line added at quantity 2 (the scraper PDP's
    // own "YOU & ME" tier) becomes two separate toothbrush-tier lines, so
    // each unit can settle at ITS OWN slot's genuine Rapi price instead of
    // both units pricing off whichever slot a naive "assume position 0"
    // rule would pick. Any units beyond the number of available slots are
    // left on the original line untouched — never dropped, never guessed
    // into a slot that doesn't exist.
    var claimedPositions = {};
    var recognized = [];
    var unrecognized = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var current = null;
      try { current = JSON.parse((line.properties && line.properties.__rapi_offer) || 'null'); } catch (e) { current = null; }
      var position = rapiScraperUpsellPositionForUpid(productId, c.scraperProductId, current && current.upid);
      if (position == null) unrecognized.push({ line: line, current: current });
      else recognized.push({ line: line, current: current, position: position });
    }

    var retagPlan = [];
    recognized.forEach(function (entry) {
      // A RECOGNIZED position with no corresponding slot at the new tier
      // (e.g. this was genuinely the second upsell but the destination
      // tier only configures one) is never guessed — left as-is.
      if (entry.position >= candidates.length) return;
      claimedPositions[entry.position] = true;
      var target = candidates[entry.position];
      var stale = !entry.current || entry.current.offer !== target.offerIdentifier || entry.current.upid !== target.upsellIdentifier;
      if (!stale) return;
      retagPlan.push({ lineKey: entry.line.key, originalQuantity: entry.line.quantity, targets: [{ target: target, quantity: entry.line.quantity }] });
    });

    var nextPosition = 0;
    function claimNextPosition() {
      while (nextPosition < candidates.length && claimedPositions[nextPosition]) nextPosition++;
      if (nextPosition >= candidates.length) return null;
      claimedPositions[nextPosition] = true;
      return candidates[nextPosition++];
    }
    unrecognized.forEach(function (entry) {
      var targets = [];
      for (var u = 0; u < entry.line.quantity; u++) {
        var target = claimNextPosition();
        if (!target) break; // no more slots — remaining units left on the line, never invented
        targets.push({ target: target, quantity: 1 });
      }
      if (!targets.length) return;
      retagPlan.push({ lineKey: entry.line.key, originalQuantity: entry.line.quantity, targets: targets });
    });
    if (!retagPlan.length) return Promise.resolve(cart);

    reconciling = true;
    var chain = Promise.resolve(cart);
    retagPlan.forEach(function (step) {
      var assignedQuantity = step.targets.reduce(function (n, t) { return n + t.quantity; }, 0);
      var remainingQuantity = step.originalQuantity - assignedQuantity;
      chain = chain
        // Suppressed — neither the removal nor (for a partial fan-out) the
        // leftover quantity is the real final state; confirmScraperMutation's
        // own nudge after each add below is the one, correct, unsuppressed
        // render for that slot's retag.
        .then(function () {
          return mutateCart(routes().change, { id: step.lineKey, quantity: remainingQuantity > 0 ? remainingQuantity : 0 }, true);
        });
      step.targets.forEach(function (t) {
        chain = chain
          .then(function () {
            return mutateCart(routes().add, {
              items: [{ id: c.scraperVariantId, quantity: t.quantity, properties: rapiScraperUpsellProperties(t.target, mainId) }]
            }, true);
          })
          .then(function (addResponse) {
            return confirmScraperMutation(addResponse, c, function (cartArg, cfgArg) {
              return findPaidScraperLineByUpid(cartArg, cfgArg, t.target.upsellIdentifier);
            }, isDiscountedScraperLine);
          });
      });
    });
    return chain.finally(function () {
      reconciling = false;
    });
  }

  // Collapses every remaining paid scraper line back into the scraper
  // PDP's OWN "quantity_breaks" bundle once the last toothbrush is removed
  // — the mirror image of the scraper-first handoff in handleScraperFirst/
  // reconcileScraperOffer above. A toothbrush-tier-tagged scraper line has
  // no main line left to belong to at that point (Rapi's discount Function
  // cross-checks the upsell against a main line carrying the SAME offer
  // id — see findRapiMainOfferId), so it would otherwise silently price at
  // retail forever. Mirrors rapiMainOfferProperties' own shape, for the
  // scraper product instead of the toothbrush. Never invents a tier: if
  // the current total scraper quantity has no configured scraper-bundle
  // offer, every line is left exactly as-is rather than guessed into an
  // approximate one.
  function reconcileScraperOwnershipReversion(cart, c) {
    var lines = findPaidScraperLines(cart, c);
    if (!lines.length) return Promise.resolve(cart);

    var scraperQty = lines.reduce(function (n, l) { return n + l.quantity; }, 0);
    var scraperOffer = resolveRapiOfferForQuantity(scraperQty, c.scraperProductId);
    if (!scraperOffer) return Promise.resolve(cart);

    // Already a single, correctly-tagged scraper-bundle main line —
    // nothing stale to fix (e.g. a later reconcile pass after this one
    // already ran).
    if (lines.length === 1) {
      var already = null;
      try { already = JSON.parse((lines[0].properties && lines[0].properties.__rapi_offer) || 'null'); } catch (e) { already = null; }
      if (already && already.main === true && already.offer === scraperOffer.offer.identifier_id) return Promise.resolve(cart);
    }

    var sessionId = findAnyRapiSessionId(cart, c.scraperVariantId);
    if (!sessionId) return Promise.resolve(cart); // never invent the session id

    reconciling = true;
    var properties = {
      __rapi_offer: JSON.stringify({ main: true, id: sessionId, offer: scraperOffer.offer.identifier_id }),
      _rapi_bundles: String(scraperOffer.bundleId),
      _rapi_market: rapiMarketCode()
    };

    // Remove every existing paid line (suppressed — none of these
    // intermediate states is the real final one), then add back exactly
    // one consolidated line at the true total quantity, tagged as the
    // scraper bundle's own main line — mirrors handleAddScraper's own
    // remove-then-readd pattern used throughout this file. The final add
    // is left unsuppressed (a genuine render), matching reconcileMainOffer's
    // own trust level for retagging a MAIN line, never confirmScraperMutation's
    // extra settlement nudge (that pattern exists for UPSELL lines, which
    // have an independently-observed one-beat settlement lag — this
    // is a main-tier quantity_breaks retag, the same kind of change
    // reconcileMainOffer already trusts the response for).
    var chain = Promise.resolve(cart);
    lines.forEach(function (line) {
      chain = chain.then(function () {
        return mutateCart(routes().change, { id: line.key, quantity: 0 }, true);
      });
    });
    chain = chain.then(function () {
      return mutateCart(routes().add, {
        items: [{ id: c.scraperVariantId, quantity: scraperQty, properties: properties }]
      });
    });
    return chain.finally(function () {
      reconciling = false;
    });
  }

  function findAnyGiftLine(cart, c) {
    return findGiftScraperLine(cart, c.scraperVariantId);
  }

  // At 3+ boxes the FREE gift is always the mandatory, sole owner of the
  // "one free scraper" promotion — but Rapi's 3-box tier MAY ALSO have its
  // own paid scraper upsell configured (checked fresh via
  // resolveRapiScraperUpsell, exactly like every other paid-scraper flow —
  // never assumed present or absent). A paid line that's ALREADY genuinely
  // tagged for THIS tier's own current upsell (e.g. deliberately added via
  // the tier-3 "Add another scraper" CTA) is left alone to coexist with the
  // gift. Any OTHER paid line — carried over from a lower tier (which can
  // now legitimately have had TWO coexisting paid lines of its own, since
  // both the 1- and 2-box tiers configure two scraper upsells), still
  // quoting an older offer/upid because nothing has retagged it since the
  // customer reached 3 boxes — is stale: at most ONE of them becomes the
  // gift (Rapi's tier-3 gift is always exactly one unit), and any further
  // stale paid line is removed outright rather than left as an
  // unreconciled, un-discounted leftover. Ensures at most one gift line
  // (findGiftScraperLine recognizes both this new Rapi-native shape and the
  // legacy _kynd_gift marker, so reconcileGiftScraper's own unmodified
  // fallback — which runs AFTER this one in refresh()'s chain, guarded on
  // `!giftLine` — never duplicates it, only ever covers the case this
  // function couldn't resolve real Rapi metadata for).
  function reconcileThreeBoxGift(cart, c, boxQty) {
    var paidLines = findPaidScraperLines(cart, c);
    var giftLine = findAnyGiftLine(cart, c);
    if (!paidLines.length && giftLine) return Promise.resolve(cart); // already exactly one, correctly settled

    var productId = primaryToothbrushProductId(cart, c);
    var tierUpsellMatch = resolveRapiScraperUpsell(boxQty, productId, c.scraperProductId);

    // At most ONE paid line may legitimately coexist as tier-3's own
    // configured extra scraper (tier 3 only ever has one upsell slot) —
    // the FIRST one already correctly tagged for it is kept.
    var keptOne = false;
    var stalePaidLines = [];
    paidLines.forEach(function (line) {
      if (!keptOne && tierUpsellMatch) {
        var current = null;
        try { current = JSON.parse((line.properties && line.properties.__rapi_offer) || 'null'); } catch (e) { current = null; }
        if (current && current.offer === tierUpsellMatch.offerIdentifier && current.upid === tierUpsellMatch.upsellIdentifier) {
          keptOne = true;
          return;
        }
      }
      stalePaidLines.push(line);
    });

    var mainId = findRapiMainOfferId(cart, c);
    var mainOffer = resolveRapiMainOffer(boxQty, productId);

    // Every OTHER stale paid line (carried over from a lower tier, which
    // can now legitimately have had TWO coexisting paid lines of its own)
    // used to be deleted outright the moment the toothbrush tier changed —
    // silently losing a scraper the customer already had in cart. Instead,
    // convert stale lines IN PLACE (retag via /cart/change.js properties,
    // never remove+re-add) into whichever of this tier's two legitimate
    // roles is still unfilled, gift first (the mandatory entitlement, never
    // lost) then the tier's own paid upsell slot (if configured and not
    // already occupied) — mirrors the exact conceptual stack: the first
    // available existing scraper becomes the FREE gift, the second becomes
    // the tier's own paid upsell. Only once BOTH roles are filled does any
    // further leftover line get removed — there is genuinely nowhere else
    // for it to go.
    var needsGift = !giftLine && !!(mainId && mainOffer);
    var needsTierUpsell = !keptOne && !!tierUpsellMatch;
    var toConvertToGift = needsGift ? (stalePaidLines.shift() || null) : null;
    var toConvertToUpsell = needsTierUpsell ? (stalePaidLines.shift() || null) : null;
    var toRemove = stalePaidLines;
    // A gift is still needed but nothing existing was available to convert
    // (the common case: the customer had zero paid scrapers) — falls back
    // to the original fresh /cart/add.js, exactly as before this fix.
    var needsFreshGiftAdd = needsGift && !toConvertToGift;

    if (!toConvertToGift && !toConvertToUpsell && !needsFreshGiftAdd && !toRemove.length) return Promise.resolve(cart);

    reconciling = true;
    var chain = Promise.resolve(cart);
    var isLastStep = toConvertToGift || toConvertToUpsell || needsFreshGiftAdd;

    // Removals first — pure cleanup, never the row a settlement check needs
    // to confirm. Suppressed unless genuinely the last mutation of the
    // whole sequence.
    toRemove.forEach(function (line, idx) {
      var isLastOverall = idx === toRemove.length - 1 && !isLastStep;
      chain = chain.then(function () {
        return mutateCart(routes().change, { id: line.key, quantity: 0 }, !isLastOverall);
      });
    });

    if (toConvertToGift) {
      chain = chain
        .then(function () {
          // `quantity` included explicitly — omitting it resets the line to
          // 1 rather than preserving it (confirmed live against the real
          // Cart API). toConvertToGift.quantity is this line's own current
          // quantity from the fresh snapshot findPaidScraperLines just read.
          return mutateCart(routes().change, { id: toConvertToGift.key, quantity: toConvertToGift.quantity, properties: rapiGiftScraperProperties(mainOffer, mainId) }, true);
        })
        .then(function (changeResponse) { return confirmScraperMutation(changeResponse, c, findAnyGiftLine, isSettledFreeGiftLine); });
    } else if (needsFreshGiftAdd) {
      // Original behavior, unchanged: no existing line to convert, so a
      // brand-new gift line is added instead.
      chain = chain
        .then(function () {
          return mutateCart(routes().add, {
            items: [{ id: c.scraperVariantId, quantity: 1, properties: rapiGiftScraperProperties(mainOffer, mainId) }]
          }, true);
        })
        .then(function (addResponse) { return confirmScraperMutation(addResponse, c, findAnyGiftLine, isSettledFreeGiftLine); });
    }

    if (toConvertToUpsell) {
      chain = chain
        .then(function () {
          // `quantity` included explicitly — same fix as toConvertToGift
          // above. toConvertToUpsell.quantity is this line's own current
          // quantity from the fresh snapshot, never assumed to be 1.
          return mutateCart(routes().change, { id: toConvertToUpsell.key, quantity: toConvertToUpsell.quantity, properties: rapiScraperUpsellProperties(tierUpsellMatch, mainId) }, true);
        })
        .then(function (changeResponse) {
          return confirmScraperMutation(changeResponse, c, function (cartArg, cfgArg) {
            return findPaidScraperLineByUpid(cartArg, cfgArg, tierUpsellMatch.upsellIdentifier);
          }, isDiscountedScraperLine);
        });
    }

    return chain.finally(function () {
      reconciling = false;
    });
  }

  // Revalidate the snapshot target before mutating. Never edit gifts, recurring
  // scrapers or split lines; mixed carts must still match the verified tier gate.
  function handleScraperFirst(c, card, target) {
    clearError(card);
    setBusy(card, true);
    fetchCart()
      .then(function (cart) {
        var current = getState(cart, c);
        if (!target) {
          if (current.kind !== 'scraper-first') throw new Error('Cart changed. Please review the current offers.');
          // The FIRST-ever toothbrush add from a scraper-only cart — unlike
          // handleAddBox/handleUpgrade (which only ever adjust an EXISTING,
          // already-tagged line), there is no prior toothbrush line to fold
          // a retag into, so the correct Rapi main-offer properties are
          // built and sent in this SAME /cart/add.js call, exactly like
          // handleAddScraper already does for a fresh paid-upsell add:
          //   - the cross-sell object (bundle/offer/target product) comes
          //     straight from the scraper's OWN currently-configured Rapi
          //     offer — never assumed, never hardcoded;
          //   - the shared per-session id is reused off the existing
          //     scraper line Rapi's own PDP widget already stamped — never
          //     invented.
          // Fail closed (same pattern as handleAddScraper): if either half
          // can't be verified, don't add an untagged toothbrush at all.
          var scraperQty = sumQty(cart, c.scraperVariantId);
          var crossSell = resolveScraperCrossSellUpsell(scraperQty, c);
          var sessionId = findAnyRapiSessionId(cart, c.scraperVariantId);
          if (!crossSell || !sessionId) throw new Error('Could not verify the current offer.');
          var mainOffer = resolveRapiMainOffer(1, crossSell.targetProductId);
          if (!mainOffer) throw new Error('Could not verify the current offer.');
          return mutateCart(routes().add, {
            items: [{ id: c.toothbrushVariantId, quantity: 1, properties: rapiMainOfferProperties({}, mainOffer, sessionId) }]
          });
        }
        if (!current.scraperTarget || JSON.stringify(current.scraperTarget) !== JSON.stringify(target)) {
          throw new Error('Cart changed. Please review the current offers.');
        }
        return mutateCart(routes().change, { id: target.lineKey, quantity: target.target });
      })
      .then(refresh)
      .catch(function (e) {
        showError(card, 'Could not update your cart. Please review your cart and try again.');
        console.error(e);
      })
      .finally(function () { setBusy(card, false); });
  }

  // 1-box -> 2-box "add another box" CTA. Distinct from handleUpgrade
  // below (which always jumps straight to 3): this increments by exactly
  // one on the selected existing line, preserving its selling plan/properties.
  //
  // Best-effort perf optimization: the DESTINATION quantity is already
  // known here, so the destination tier's Rapi main-offer retag is folded
  // into this SAME /cart/change.js body when it can be resolved up front,
  // instead of always relying on a second, separate reconcileMainOffer
  // round trip after the fact. reconcileMainOffer stays in refresh()'s
  // chain unchanged as the safety net — if this can't resolve (no tag on
  // the line yet, config miss), the plain quantity-only body is sent and
  // reconcileMainOffer detects/repairs it exactly as before. Never a
  // hardcoded offer id — same resolveRapiMainOffer used everywhere else.
  //
  // Also reused (unmodified) by the native-quantity fetch interception
  // below (see NATIVE_CHANGE_RE / augmentNativeToothbrushQuantityRequest)
  // to fold the SAME retag into Dawn's own native +/- stepper request —
  // this function doesn't need to know or care which caller built `body`.
  function foldMainOfferIntoChangeBody(body, line, targetQty) {
    var raw = line.properties && line.properties.__rapi_offer;
    if (!raw) return body;
    var current;
    try { current = JSON.parse(raw); } catch (e) { return body; }
    if (!current || current.id == null) return body;
    var target = resolveRapiMainOffer(targetQty, line.product_id);
    if (!target || current.offer === target.offerIdentifier) return body;
    body.properties = rapiMainOfferProperties(line.properties, target, String(current.id));
    return body;
  }

  // Resolves WHICH cart line Dawn's own native /cart/change request body
  // refers to. Dawn's drawer quantity stepper identifies its target by
  // `line` (a 1-based POSITIONAL index into cart.items — confirmed live via
  // HAR capture: `{"line":"1","quantity":"2",...}`), not by `id`/key; other
  // native or first-party callers may still use `id` (a variant id or line
  // key), so both are supported. Returns null (never guesses) when neither
  // is present or resolvable against the given cart.
  function resolveNativeChangeTargetLine(cart, body) {
    if (body.id != null) {
      return cart.items.find(function (line) {
        return String(line.key) === String(body.id) || String(line.variant_id) === String(body.id);
      }) || null;
    }
    if (body.line != null) {
      var idx = parseInt(body.line, 10) - 1;
      return idx >= 0 && idx < cart.items.length ? cart.items[idx] : null;
    }
    return null;
  }

  // Makes Dawn's own native toothbrush quantity change ATOMIC: folds the
  // correct Rapi main-offer retag into the SAME request the native stepper
  // is already sending, using the exact same foldMainOfferIntoChangeBody
  // helper the explicit CTAs use — never a second, separate mechanism.
  // Confirmed live (HAR capture) that reacting AFTER a correct native
  // quantity change with a separate property-only retag left a window for
  // a second, independent reconciliation pass to run against a snapshot
  // that could disagree with the first, retagging the line back to a
  // stale tier. Folding into the ORIGINAL request removes that window
  // entirely for the common case, rather than trying to coordinate two
  // requests more carefully.
  //
  // Returns a Promise resolving to the augmented JSON body string, or null
  // — meaning "send Dawn's original request completely unchanged" — for
  // every case this can't safely handle: not a quantity change, not our
  // toothbrush, no existing Rapi session id to preserve (never invented),
  // or no resolvable offer for the destination quantity (never guessed).
  function augmentNativeToothbrushQuantityRequest(bodyText, c) {
    var body;
    try { body = JSON.parse(bodyText); } catch (e) { return Promise.resolve(null); }
    if (!body || body.quantity == null) return Promise.resolve(null);
    var destQty = parseInt(body.quantity, 10);
    if (!(destQty >= 1)) return Promise.resolve(null); // 0 = removal — not this path's concern
    return fetchCart().then(function (cart) {
      var line = resolveNativeChangeTargetLine(cart, body);
      if (!line || !matchesVariant(line, brushIds(c))) return null; // not our toothbrush line
      foldMainOfferIntoChangeBody(body, line, destQty); // mutates body.properties in place, if resolvable
      if (!body.properties) return null; // nothing to fold (untagged line, already correct, or no config match)
      return JSON.stringify(body);
    }).catch(function () { return null; }); // any failure — fail open, never guess
  }

  // Stability rollback: unlimited quantity on the FREE GIFT line or a
  // genuine toothbrush-tier UPSELL line (both the tier-3 "Add another" CTA
  // and native "+" on an already-existing one) has been abandoned — Rapi's
  // discounted scraper metadata does not reliably survive repeated
  // quantity/add operations on those two specific line types. This is the
  // network-level backstop: neutralizes any /cart/change.js request that
  // would still increase ONE OF THOSE TWO line types' quantity beyond what
  // it already is (a stale cached DOM, a directly-issued request, etc.) and
  // does nothing else — no reroute into an add/upgrade flow, no second
  // pricing system. A genuine decrement or removal (destQty <= current
  // quantity) is left completely untouched.
  //
  // FIXED (live bug): this used to neutralize growth on ANY line matching
  // the scraper's variant_id, with no check beyond that — so a customer's
  // own normal paid scraper line, or a scraper-bundle quantity-break line
  // (e.g. "Full House"), had its native "+" silently no-op'd too. Only the
  // free gift or a positively-identified upsell line (isRapiScraperUpsellProperties)
  // is ours to cap; every other scraper line's quantity is fully
  // customer-controlled and must pass through untouched.
  function interceptScraperQuantityIncrement(bodyText, c) {
    var body;
    try { body = JSON.parse(bodyText); } catch (e) { return Promise.resolve(null); }
    if (!body || body.quantity == null) return Promise.resolve(null);
    var destQty = parseInt(body.quantity, 10);
    return fetchCart().then(function (cart) {
      var line = resolveNativeChangeTargetLine(cart, body);
      if (!line || line.variant_id !== c.scraperVariantId) return null; // not a scraper line at all
      var isGift = isRapiNativeGiftProperties(line.properties) || (line.properties && line.properties[GIFT_PROP_KEY] === GIFT_PROP_VALUE);
      if (!isGift && !isRapiScraperUpsellProperties(line.properties)) return null; // not ours to cap — normal customer/scraper-bundle line, leave fully customer-controlled
      if (!(destQty > line.quantity)) return null; // only genuine increments — decrements/removals pass through untouched
      body.quantity = line.quantity; // neutralize the native request — no gift/upsell line may grow via native "+"
      return JSON.stringify(body);
    }).catch(function () { return null; }); // any failure — fail open, never guess
  }

  function handleAddBox(c, card, subscriptionTarget, btn) {
    clearError(card);
    setBusy(card, true, btn, 'Adding…');
    fetchCart()
      .then(function (cart) {
        var line = findUpgradeableToothbrushLine(cart, brushIds(c), subscriptionTarget);
        if (!line) throw new Error('Toothbrush line not found in cart.');
        var targetQty = line.quantity + 1;
        return mutateCart(routes().change, foldMainOfferIntoChangeBody({ id: line.key, quantity: targetQty }, line, targetQty));
      })
      .then(refresh)
      .catch(function (e) {
        showError(card, 'Could not add another box. Please try again.');
        console.error(e);
      })
      .finally(function () {
        setBusy(card, false, btn);
      });
  }

  function handleUpgrade(c, card, subscriptionTarget, btn) {
    clearError(card);
    setBusy(card, true, btn, 'Upgrading…');
    fetchCart()
      .then(function (cart) {
        var line = findUpgradeableToothbrushLine(cart, brushIds(c), subscriptionTarget);
        if (!line) throw new Error('Toothbrush line not found in cart.');
        var currentQty = subscriptionTarget ? sumQty(cart, brushIds(c)) : sumOneTimeQty(cart, brushIds(c));
        var targetQty = line.quantity + (3 - currentQty);
        return mutateCart(routes().change, foldMainOfferIntoChangeBody({ id: line.key, quantity: targetQty }, line, targetQty));
      })
      .then(refresh)
      .catch(function (e) {
        showError(card, 'Could not upgrade your order. Please try again.');
        console.error(e);
      })
      .finally(function () {
        setBusy(card, false, btn);
      });
  }

  // Compact product-card row: real image + title/subtitle + a small CTA,
  // used for every upsell offer (never for the success/reward state,
  // which has its own lighter layout below). `image` is always a real
  // Shopify product image URL passed in from cfg() (sourced from
  // kynd-cart-upsells.liquid) — never a hardcoded asset.
  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  // Display only: the existing verified quote already includes paid-to-free replacement.
  // Unpriced/discount-code/subscription/unknown-market states make no shipping promise.
  function heroUpgradeBenefits(cart, c, quote) {
    var result = { gift: false, shipping: false, savings: '' };
    if (!quote || !Number.isFinite(quote.deltaCents) || quote.deltaCents <= 0) return result;
    if (Number.isFinite(quote.percentage) && quote.percentage > 0 && quote.percentage <= 100) {
      result.savings = 'SAVE ' + quote.percentage + '%';
    }
    var resolved = resolveRapiOfferForQuantity(3, primaryToothbrushProductId(cart, c));
    var gifts = resolved && resolved.offer && resolved.offer.free_gifts;
    result.gift = !!(findRapiMainOfferId(cart, c) && !findAnyGiftLine(cart, c) && Array.isArray(gifts) && gifts.some(function (gift) {
      return String(gift.shopify_product_id) === String(c.scraperProductId) && Number(gift.quantity) === 1 &&
        !gift.apply_only_for_subscriptions && (gift.shopify_variant_ids == null ||
          (Array.isArray(gift.shopify_variant_ids) && gift.shopify_variant_ids.map(String).indexOf(String(c.scraperVariantId)) !== -1));
    }));
    var progress = document.querySelector('#CartDrawer [data-shipping-threshold]');
    var goal = progress && Number(progress.dataset.shippingThreshold);
    var knownItems = cart.items.every(function (line) {
      return matchesVariant(line, brushIds(c)) || String(line.product_id) === String(c.scraperProductId);
    });
    result.shipping = !!(result.gift && knownItems && progress && Number.isFinite(goal) && goal > 0 &&
      progress.dataset.shippingCurrency === cart.currency && Number.isFinite(cart.total_price) &&
      cart.total_price + quote.deltaCents >= goal);
    return result;
  }

  function heroBenefitsHtml(benefits) {
    var icon = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.5 4.5l-7 7-4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return (benefits.savings ? '<p class="kynd-upsell__hero-saving">' + escapeHtml(benefits.savings) + '</p>' : '') +
      (benefits.gift || benefits.shipping ? '<div class="kynd-upsell__hero-benefits">' +
        (benefits.gift ? '<span>' + icon + '<strong>FREE</strong> copper scraper</span>' : '') +
        (benefits.shipping ? '<span>' + icon + '<strong>FREE</strong> shipping</span>' : '') + '</div>' : '');
  }

  function productRowHtml(opts) {
    var thumb = opts.image
      ? '<img class="kynd-upsell__thumb" src="' + escapeHtml(opts.image) + '" alt="" loading="eager" width="56" height="56">'
      : '<span class="kynd-upsell__thumb kynd-upsell__thumb--empty" aria-hidden="true"></span>';
    if (opts.hero && opts.hero.gift && opts.giftImage) {
      thumb = '<span class="kynd-upsell__bundle-thumb">' + thumb +
        '<img class="kynd-upsell__bonus-thumb" src="' + escapeHtml(opts.giftImage) + '" alt="" width="28" height="28" loading="eager"></span>';
    }
    var title = escapeHtml(opts.title);
    // URLs are localized Shopify Liquid product/variant URLs, never CTA links.
    if (opts.url) {
      var href = escapeHtml(opts.url);
      thumb = '<a class="kynd-upsell__image-link" href="' + href + '" aria-label="' + title + '">' + thumb + '</a>';
      title = '<a href="' + href + '">' + title + '</a>';
    }
    var ctaClass = 'kynd-upsell__cta';
    return (
      '<div class="kynd-upsell__row kynd-upsell__row--product' + (opts.hero ? ' kynd-upsell__row--hero' : '') + '" data-role="' +
      opts.role +
      '"' +
      '>' +
      thumb +
      '<div class="kynd-upsell__row-body">' +
      '<p class="kynd-upsell__title">' + title + '</p>' +
      (opts.subtitle ? '<p class="kynd-upsell__subtitle">' + escapeHtml(opts.subtitle) + '</p>' : '') +
      (opts.hero ? heroBenefitsHtml(opts.hero) : '') +
      '<button type="button" class="' + ctaClass + '" data-action="' + opts.action + '">' + opts.ctaLabel + '</button>' +
      '</div>' +
      '</div>'
    );
  }

  // `boxQty` picks the title/copy TEMPLATE for the row already being
  // shown (unchanged eligibility/timing, decided entirely by the caller's
  // state.showScraperOffer): at 3+ boxes the free gift already exists, so
  // the paid row is always framed as "additional". At 1/2 boxes it's
  // "Complete the routine" for the FIRST scraper offered and "Add another
  // copper scraper" once one already exists (live Rapi config now
  // configures TWO scraper upsells on those tiers — see
  // resolveNextRapiScraperUpsell above).
  //
  // Which SPECIFIC upsell to price/tag comes from resolveNextRapiScraperUpsell
  // (the next configured upsell not yet represented by a real cart line),
  // never resolveRapiScraperUpsell's single "first match" directly, so
  // this always offers whichever tier the customer hasn't taken yet.
  //
  // Subtitle/CTA show the live Rapi upsell price via the generic
  // rapiUpsellDisplayPricing() — handles EITHER discount_type this store's
  // config now uses ("specific", e.g. EUR/US/GB, or "percentage", e.g. the
  // ROW market) without knowing in advance which one the current market's
  // bundle configures. Fails open to the original generic, non-priced
  // copy whenever any part of that resolution isn't cleanly available —
  // never a partial/guessed price, never a second network request (cart
  // is already in hand from the same reconciliation pass that decided to
  // show this row).
  function scraperOfferRowHtml(c, cart, boxQty) {
    var hasExistingPaidScraper = findPaidScraperLines(cart, c).length > 0;
    var isThirdTier = boxQty >= 3;
    var title = (isThirdTier || hasExistingPaidScraper) ? 'Add another copper scraper' : 'Complete the routine';
    var subtitle = 'Complete your routine · Special cart price';
    var ctaLabel = 'Add scraper';
    var offer = resolveNextRapiScraperUpsell(cart, c, boxQty);
    var pricing = offer ? rapiUpsellDisplayPricing(offer, c) : null;
    if (pricing != null) {
      var priceText = formatMoney(pricing.finalPriceCents, cart);
      var savingText = pricing.savingsCents != null ? ' · Save ' + formatMoney(pricing.savingsCents, cart) : '';
      var percentPrefix = pricing.discountType === 'percentage' ? formatRapiPercent(pricing.discountPercent) + ' OFF · ' : '';
      if (isThirdTier) {
        subtitle = pricing.discountType === 'percentage'
          ? percentPrefix + 'Special 3-box price · ' + priceText
          : 'Special 3-box price · ' + priceText + savingText;
      } else if (hasExistingPaidScraper) {
        subtitle = percentPrefix + 'Add another for ' + priceText + savingText;
      } else {
        subtitle = pricing.discountType === 'percentage'
          ? percentPrefix + 'Add the copper scraper for ' + priceText
          : 'Add the copper scraper for ' + priceText + savingText;
      }
      ctaLabel = (isThirdTier || hasExistingPaidScraper ? 'Add another · ' : 'Add scraper · ') + priceText;
    }
    return productRowHtml({
      role: 'scraper-offer',
      image: c.scraperImage,
      url: c.scraperUrl,
      title: title,
      subtitle: subtitle,
      ctaLabel: ctaLabel,
      action: 'add-scraper'
    });
  }

  function buildCard(state, cart, c) {
    var card = document.createElement('div');
    card.className = 'kynd-upsell';
    card.id = 'kynd-upsell-card';

    if (state.kind === 'success') {
      card.classList.add('kynd-upsell--success');
      // Trace the initial subscription order; reward copy still requires actual
      // current-cart confirmation. This does not create a recurring scraper.
      if (state.isSubscriptionInitialOrder) {
        card.setAttribute('data-kynd-subscription-initial-order', 'true');
      }
      var checks = [];
      if (!state.hasGiftScraper && state.hasBundleSavings) checks.push('Bundle savings applied');
      else if (state.hasRealDiscount) checks.push('15% off toothbrushes');
      if (state.hasGiftScraper) checks.push('FREE copper tongue scraper');
      if (state.freeShippingReached) checks.push('FREE shipping');
      var checksHtml = checks
        .map(function (label) {
          return (
            '<span class="kynd-upsell__check">' +
            '<svg class="kynd-upsell__check-icon" viewBox="0 0 16 16" aria-hidden="true">' +
            '<path d="M13.5 4.5l-7 7-4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
            label +
            '</span>'
          );
        })
        .join('');
      var heading = state.hasGiftScraper && state.freeShippingReached ? 'Best value unlocked' : state.boxQty + '-BOX BUNDLE ACTIVE';
      var status = '';
      if (state.hasGiftScraper && state.hasPaidScraper) {
        status = 'Your free scraper is included. Additional scrapers are charged as shown above.';
      }
      // state.scraperTarget (the 2/4-unit mixed-scraper ladder) and
      // state.showScraperOffer (a genuinely tier-specific paid upsell, e.g.
      // the tier-3 additional scraper once the free gift settles) describe
      // mutually exclusive cart states — never both at once — but each
      // reuses its own existing row-building helper unmodified.
      var extraRowHtml = state.scraperTarget
        ? productRowHtml(scraperUpgradeOffer(state.scraperTarget, c, cart))
        : state.showScraperOffer
          ? scraperOfferRowHtml(c, cart, state.boxQty)
          : '';
      card.innerHTML =
        '<p class="kynd-upsell__eyebrow kynd-upsell__eyebrow--success">' + heading + '</p>' +
        (checksHtml ? '<div class="kynd-upsell__checks">' + checksHtml + '</div>' : '') +
        (status ? '<p class="kynd-upsell__subtitle" role="status">' + status + '</p>' : '') +
        (extraRowHtml ? extraRowHtml + '<p class="kynd-upsell__error" role="alert"></p>' : '');
      return card;
    }

    // Only the two 2-box states get the distinct "best value" framing.
    var eyebrow = (state.kind === 'upgrade-basic' || state.kind === 'upgrade-strong')
      ? 'Unlock best value'
      : state.kind === 'scraper-first'
        ? 'Complete the routine'
        : 'Upgrade your order';
    var html = '<p class="kynd-upsell__eyebrow">' + eyebrow + '</p>';

    if (state.kind === 'scraper-first') {
      if (state.scraperBestValue) {
        // Live percentage, never a hardcoded "20%" — reads the scraper's
        // OWN qty-4 FULL HOUSE offer fresh, the same generic resolver used
        // everywhere else in this file.
        var fullHouseOffer = resolveRapiMainOffer(4, c.scraperProductId);
        var fullHouseBenefits = [];
        if (fullHouseOffer && fullHouseOffer.discountType === 'percentage' && typeof fullHouseOffer.discountValue === 'number') {
          fullHouseBenefits.push(formatRapiPercent(fullHouseOffer.discountValue) + ' off scrapers');
        }
        fullHouseBenefits.push('FREE shipping');
        var fullHouseChecksHtml = fullHouseBenefits.map(function (label) {
          return '<span class="kynd-upsell__check">' +
            '<svg class="kynd-upsell__check-icon" viewBox="0 0 16 16" aria-hidden="true">' +
            '<path d="M13.5 4.5l-7 7-4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' + label + '</span>';
        }).join('');
        html += '<p class="kynd-upsell__eyebrow kynd-upsell__eyebrow--success">FULL HOUSE UNLOCKED</p>' +
          '<div class="kynd-upsell__checks">' + fullHouseChecksHtml + '</div>';
      }
      html += state.offers.map(productRowHtml).join('');
    } else if (state.kind === 'add-box') {
      var upgrade12 = rapiTierUpgradeDisplay(cart, c, 1, 2);
      html += productRowHtml({
        role: 'upgrade',
        image: c.toothbrushImage,
        url: c.toothbrushUrl,
        title: 'Add a 2nd box',
        subtitle: upgrade12
          ? 'Only ' + formatMoney(upgrade12.deltaCents, cart) + ' more · Save ' + upgrade12.percentage + '%'
          : 'Save 10% when you stock up.',
        ctaLabel: 'Add 2nd box',
        action: 'add-box'
      });
      if (state.showScraperOffer) {
        html += scraperOfferRowHtml(c, cart, state.boxQty);
      }
    } else if (state.kind === 'upgrade-basic') {
      var upgrade23basic = rapiTierUpgradeDisplay(cart, c, 2, 3);
      html += productRowHtml({
        role: 'upgrade',
        image: c.toothbrushImage,
        url: c.toothbrushUrl,
        title: 'Add your 3rd box',
        subtitle: upgrade23basic
          ? 'Only ' + formatMoney(upgrade23basic.deltaCents, cart) + ' more'
          : '',
        hero: heroUpgradeBenefits(cart, c, upgrade23basic),
        giftImage: c.scraperImage,
        ctaLabel: upgrade23basic ? 'Upgrade · ' + formatMoney(upgrade23basic.deltaCents, cart) : 'Upgrade to 3',
        action: 'upgrade'
      });
      if (state.showScraperOffer) {
        html += scraperOfferRowHtml(c, cart, state.boxQty);
      }
    } else if (state.kind === 'upgrade-strong') {
      var upgrade23strong = rapiTierUpgradeDisplay(cart, c, 2, 3);
      html += productRowHtml({
        role: 'upgrade',
        image: c.toothbrushImage,
        url: c.toothbrushUrl,
        title: 'Add your 3rd box',
        subtitle: upgrade23strong
          ? 'Only ' + formatMoney(upgrade23strong.deltaCents, cart) + ' more'
          : '',
        hero: heroUpgradeBenefits(cart, c, upgrade23strong),
        giftImage: c.scraperImage,
        ctaLabel: upgrade23strong ? 'Upgrade · ' + formatMoney(upgrade23strong.deltaCents, cart) : 'Upgrade to 3',
        action: 'upgrade'
      });
      if (state.showScraperOffer) html += scraperOfferRowHtml(c, cart, state.boxQty);
    }

    html += '<p class="kynd-upsell__error" role="alert"></p>';
    card.innerHTML = html;
    return card;
  }

  function render(cart) {
    updateShippingProgress(cart);
    var c = cfg();
    if (!c) return;
    var state = getState(cart, c);
    var upgradeQuote = state.kind === 'add-box' ? rapiTierUpgradeDisplay(cart, c, 1, 2) :
      (state.kind === 'upgrade-basic' || state.kind === 'upgrade-strong') ? rapiTierUpgradeDisplay(cart, c, 2, 3) : null;
    var renderKey = JSON.stringify([state, upgradeQuote, cart.currency, c.toothbrushImage, c.scraperImage, c.toothbrushUrl, c.scraperUrl]);
    if (c.el.__kyndRenderKey === renderKey) return;
    c.el.__kyndRenderKey = renderKey;
    c.el.innerHTML = '';
    if (state.kind === 'none') {
      c.el.hidden = true;
      return;
    }
    c.el.hidden = false;
    var card = buildCard(state, cart, c);
    c.el.appendChild(card);

    card.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var action = btn.getAttribute('data-action');
      if (action === 'add-toothbrushes') handleScraperFirst(c, card);
      if (action === 'upgrade-scrapers') handleScraperFirst(c, card, state.scraperTarget);
      if (action === 'add-scraper') handleAddScraper(c, card, btn);
      if (action === 'add-box') handleAddBox(c, card, state.subscriptionTarget, btn);
      if (action === 'upgrade') handleUpgrade(c, card, state.subscriptionTarget, btn);
    });

    refreshSymbolShrink();
  }

  var snapshotElement = null;
  function renderSnapshot() {
    var c = cfg();
    if (!c || c.el === snapshotElement) return;
    snapshotElement = c.el;
    try {
      var cart = JSON.parse(c.el.getAttribute('data-cart'));
      if (cart && Array.isArray(cart.items)) render(cart);
    } catch (e) {
      // Old/cached section HTML can omit the snapshot; refresh remains the fallback.
    }
  }

  // ONE reconciliation transaction at a time, full stop — regardless of
  // which of the eight call sites triggered it (native pub-sub, the
  // cartQuantityUpdated DOM event, the /cart/update.js fetch-wrap gap,
  // page-load init(), or one of our own CTA handlers calling refresh()
  // with an already-fresh cart). Previously a suppliedCart call (every CTA
  // handler) skipped this gate entirely and ran as a fully independent
  // chain, sharing only the `reconciling` mutex — which is only checked at
  // each reconcile* function's own entry, not enough to stop two
  // independent chains from each deciding-and-mutating off two different
  // cart snapshots. And the non-suppliedCart path only queued a trailing
  // pass when `pendingElement !== c.el` — always false in the normal case
  // (one #kynd-cart-upsells element per page), so a genuinely later
  // customer action arriving mid-cycle was silently dropped rather than
  // queued. Both are closed by making every call funnel through this same
  // pendingRefresh gate and by queueing on simple idle/running state
  // instead of element identity.
  var pendingRefresh = null;
  var refreshQueued = false;
  function refresh(cart) {
    var c = cfg();
    if (!c) return Promise.resolve();
    renderSnapshot();
    var suppliedCart = cart && Array.isArray(cart.items);
    if (pendingRefresh) {
      refreshQueued = true;
      return pendingRefresh;
    }
    var work = (suppliedCart ? Promise.resolve(cart) : fetchCart())
      .then(function (currentCart) {
        // A replaced section is newer than a read started for the old section.
        var currentConfig = cfg();
        if (!currentConfig || currentConfig.el !== c.el) return;
        // Show known state immediately; reconciliation never blocks display.
        render(currentCart);
        return reconcileMainOffer(currentCart, c)
          .then(function (afterMainCart) {
            return reconcileScraperOffer(afterMainCart || currentCart, c);
          })
          .then(function (afterOfferCart) {
            return reconcileGiftScraper(afterOfferCart || currentCart, c);
          })
          .then(function (reconciledCart) {
            render(reconciledCart || currentCart);
          });
      })
      .catch(function (e) {
        console.error('kynd-cart-upsells refresh failed', e);
      });
    pendingRefresh = work;
    work.then(function () {
      if (pendingRefresh !== work) return;
      pendingRefresh = null;
      if (refreshQueued) {
        refreshQueued = false;
        // Never inherit the cart snapshot that triggered THIS pass — the
        // queued trailing pass always calls refresh() with no argument,
        // forcing its own brand-new fetchCart(), regardless of whether
        // this pass itself was suppliedCart.
        refresh();
      }
    });
    return work;
  }

  function discountTitles(cart) {
    return (cart.cart_level_discount_applications || [])
      .map(function (d) {
        return d.title;
      })
      .sort();
  }

  // --- Applied discount codes: the native theme's cart-drawer footer
  // renders each applied code as a read-only ".badge" pill (part of the
  // "subtotals" block markup) with no way to remove one. Shopify's own
  // Ajax Cart API does support this — POSTing /cart/update.js with
  // `discount` set to a comma-joined list of the codes to KEEP re-applies
  // exactly that set (confirmed via live testing) — so this adds a small
  // × control using that real mechanism.
  //
  // IMPORTANT: the exact same ".badge" markup/class is reused by the theme
  // for THREE different things: (1) real customer-entered discount codes,
  // (2) automatic per-line discounts (the Rapi/Shopify BXGY scraper and
  // quantity-break offers) shown inside each cart item, and (3) — when the
  // "display product discounts" block setting is on — those same automatic
  // offers again, rendered a second time inside the footer's combined
  // discounts panel. A removable code and an automatic discount are NOT
  // distinguishable by class or DOM position alone. What IS reliable:
  // cart.cart_level_discount_applications only ever contains genuine
  // order-level applications — on this store that's exactly the set of
  // customer codes (BEKYND, WELCOMEBACK20, PLASTICFREE, collabs, ...),
  // since none of the active automatic offers are order-class (they're all
  // product/line-level, so Shopify never puts them in this list). So: only
  // ever attach the × control to a badge whose visible title matches one of
  // those real applied-code titles — automatic/BXGY badges are left alone.
  function enhanceDiscountBadges() {
    fetchCart()
      .then(function (cart) {
        var removableTitles = (cart.cart_level_discount_applications || []).map(function (d) {
          return d.title;
        });
        if (!removableTitles.length) return;
        var badges = document.querySelectorAll('#CartDrawer .cart-drawer__discounts .badge');
        badges.forEach(function (badge) {
          if (badge.querySelector('.kynd-discount-remove')) return;
          var labelEl = badge.querySelector('span');
          var label = labelEl ? labelEl.textContent.trim() : badge.textContent.trim();
          var matchedTitle = null;
          for (var i = 0; i < removableTitles.length; i++) {
            if (label.indexOf(removableTitles[i]) === 0) {
              matchedTitle = removableTitles[i];
              break;
            }
          }
          if (!matchedTitle) return; // automatic/product discount — never removable
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'kynd-discount-remove';
          btn.setAttribute('aria-label', 'Remove discount code');
          btn.textContent = '×';
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            removeDiscountCode(matchedTitle, btn);
          });
          badge.appendChild(btn);
        });
      })
      .catch(function (e) {
        console.error('kynd-cart-upsells enhanceDiscountBadges failed', e);
      });
  }

  function removeDiscountCode(title, btn) {
    btn.disabled = true;
    fetchCart()
      .then(function (cart) {
        var titles = (cart.cart_level_discount_applications || []).map(function (d) {
          return d.title;
        });
        var remaining = titles.filter(function (t) {
          return t !== title;
        }).join(',');
        return mutateCart(routes().update, { discount: remaining });
      })
      .then(fetchCart)
      .then(refresh)
      .catch(function (e) {
        console.error('Could not remove discount code', e);
        btn.disabled = false;
      });
  }

  function enhanceDrawerUI() {
    enhanceDiscountForm();
    enhanceDiscountCollapse();
    enhanceDiscountBadges();
  }

  // --- Discount field: collapsed by default behind an "Add discount code"
  // toggle row, purely a show/hide of the existing native form — no
  // discount functionality is added, removed, or altered. Runs once per
  // drawer render (form re-renders inside .drawer__footer on every cart
  // mutation, so this re-attaches idempotently each time via the
  // dataset guard).
  //
  // Open/closed state is mirrored to sessionStorage (per-tab, UI-only —
  // never read as part of any commercial/cart logic) so it survives the
  // footer re-rendering after a quantity change or upsell click instead
  // of always resetting to collapsed. Wrapped defensively: private
  // browsing or storage-disabled contexts just fall back to "always
  // starts collapsed," never an error.
  var DISCOUNT_OPEN_KEY = 'kyndDiscountOpen';

  function getDiscountOpenPref() {
    try {
      return sessionStorage.getItem(DISCOUNT_OPEN_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  function setDiscountOpenPref(open) {
    try {
      sessionStorage.setItem(DISCOUNT_OPEN_KEY, open ? 'true' : 'false');
    } catch (e) {
      /* storage unavailable — collapse state just won't persist */
    }
  }

  function enhanceDiscountCollapse() {
    var container = document.querySelector('#CartDrawer .cart-discount');
    var form = container && container.querySelector('.cart-discount-form');
    if (!container || !form || container.dataset.kyndCollapseWired === 'true') return;
    container.dataset.kyndCollapseWired = 'true';

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'kynd-discount-toggle';
    toggle.innerHTML =
      '<span>Add discount code</span>' +
      '<svg class="kynd-discount-toggle__chevron" viewBox="0 0 16 16" aria-hidden="true">' +
      '<path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    toggle.setAttribute('aria-controls', 'KyndDrawerDiscountForm');
    form.id = 'KyndDrawerDiscountForm';
    container.insertBefore(toggle, form);

    if (getDiscountOpenPref()) {
      container.classList.add('kynd-discount--open');
    }

    toggle.setAttribute('aria-expanded', String(container.classList.contains('kynd-discount--open')));
    toggle.addEventListener('click', function () {
      var opening = !container.classList.contains('kynd-discount--open');
      container.classList.toggle('kynd-discount--open', opening);
      toggle.setAttribute('aria-expanded', String(opening));
      setDiscountOpenPref(opening);
      if (opening) {
        var input = form.querySelector('[name="cart-discount-field"]');
        if (input) input.focus();
      }
    });
  }

  // --- Discount code: enhance the native cart-discount-field to apply via
  // background fetch (Shopify's own /discount/ endpoint) instead of a full
  // page navigation, then re-render the section and compare cart state to
  // report success/failure. No client-side price simulation.
  function enhanceDiscountForm() {
    var form = document.querySelector('#CartDrawer .cart-discount-form');
    if (!form || form.dataset.kyndEnhanced === 'true') return;
    form.dataset.kyndEnhanced = 'true';
    form.removeAttribute('onsubmit');

    var input = form.querySelector('[name="cart-discount-field"]');
    var errorEl = form.querySelector('.cart-discount-form__error');
    var errorElDefaultText = errorEl ? errorEl.textContent : '';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var code = (input && input.value || '').trim();
      if (!code) {
        if (errorEl) {
          errorEl.textContent = errorElDefaultText;
          errorEl.style.display = 'block';
        }
        return;
      }
      if (errorEl) errorEl.style.display = 'none';
      var submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      fetchCart()
        .then(function (before) {
          return fetch('/discount/' + encodeURIComponent(code), { credentials: 'include' })
            .then(function () {
              return fetch(
                '/?sections=' + allSectionIds().join(','),
                { headers: { Accept: 'application/json' } }
              );
            })
            .then(function (r) {
              return r.json();
            })
            .then(function (sections) {
              renderSections(sections);
              return fetchCart();
            })
            .then(function (after) {
              // Compare sorted discount CODES + the aggregate total, not the
              // full JSON-stringified application objects: Shopify can
              // reorder cart_level_discount_applications and recompute
              // per-discount fields (key, total_allocated_amount) between
              // requests even when nothing actually changed, which made a
              // naive full-object comparison here false-positive "applied"
              // for a genuinely invalid code (confirmed via live testing —
              // it silently swallowed the error).
              var applied = JSON.stringify(discountTitles(after)) !== JSON.stringify(discountTitles(before)) ||
                after.total_discount !== before.total_discount;
              if (!applied) {
                var newErrorEl = document.querySelector('#CartDrawer .cart-discount-form__error');
                if (newErrorEl) {
                  // The native element's baked-in copy ("Please enter a
                  // discount code!") is written for the EMPTY-input case
                  // only (it's what Dawn/Shrine shows after a full-page
                  // round trip with no other message available). A
                  // non-empty code that Shopify simply didn't apply is a
                  // different, more common case — swap in accurate copy
                  // for it rather than showing the empty-input message.
                  newErrorEl.textContent = 'That discount code is invalid or has expired.';
                  newErrorEl.style.display = 'block';
                }
              }
              enhanceDrawerUI();
              return refresh();
            });
        })
        .catch(function (err) {
          console.error(err);
          if (errorEl) errorEl.style.display = 'block';
        })
        .finally(function () {
          var btn = document.querySelector('#CartDrawer .cart-discount-form button[type="submit"]');
          if (btn) btn.disabled = false;
        });
    });
  }

  // --- Wiring.
  //
  // 1) & 2) below are the two native, theme-dispatched signals the drawer's
  // own <cart-drawer-items> component uses for itself (confirmed by reading
  // main.js directly). They correctly cover: product-page add-to-cart, the
  // drawer's own +/- quantity stepper, item removal from the drawer, and our
  // own scraper-add/upgrade buttons.
  //
  // 3) The standalone /cart page renders a DIFFERENT native component,
  // <cart-items> (not <cart-drawer-items>), which submits quantity/removal
  // changes to routes.cart_update_url (/cart/update.js) — confirmed via
  // main.js's string table and live testing, which also confirmed this
  // component does NOT publish PUB_SUB_EVENTS.cartUpdate or dispatch
  // cartQuantityUpdated the way the drawer's component does. Signals 1) and
  // 2) alone therefore leave #kynd-cart-upsells (present but hidden inside
  // the drawer markup on every page) stale after a /cart-page quantity edit,
  // until something else triggers a refresh.
  //
  // A general-purpose window.fetch monkey-patch was tested and removed in
  // the previous hardening pass because it wasn't needed for any of the
  // flows above. This is the one remaining gap it would have covered, so we
  // reintroduce the narrowest possible version of it: filtered to ONLY
  // /cart/update(.js) for the refresh()-triggering purpose below — NOT
  // /cart/change(.js) too, because /cart/change.js is what the DRAWER's own
  // component already uses for its (already-covered-by-signal-1/2) quantity
  // changes; matching it here for THAT purpose would double-refresh every
  // drawer action. /cart/update.js is unique to the standalone cart page's
  // native component, so filtering to it alone closes exactly this one gap.
  // Guarded against our own mutateCart() calls via the __kyndOwnMutation
  // marker (mutateCart doesn't currently use update.js, but this keeps the
  // wrap correct if that ever changes) so we never fire a redundant
  // parallel refresh on top of mutateCart's own .then(refresh) chains.
  //
  // /cart/change(.js) IS separately matched below (NATIVE_CHANGE_RE), for a
  // DIFFERENT purpose: making Dawn's own native toothbrush quantity request
  // atomic (see augmentNativeToothbrushQuantityRequest). Confirmed live via
  // HAR capture: a correct native quantity change followed by our own
  // separate, later property-only retag left a window for a second,
  // independent reconciliation pass to retag the line back to a stale
  // tier. Folding the retag into the SAME request Dawn is already sending
  // removes that window for the common case; reconcileMainOffer remains,
  // unchanged, as the safety net for whatever this can't resolve.
  var nativeFetch = window.fetch;
  var CART_MUTATION_RE = /\/cart\/update(\.js)?(\?|$)/;
  var SHIPPING_MUTATION_RE = /\/cart\/(add|change|clear)(\.js)?(\?|$)/;
  var NATIVE_CHANGE_RE = /\/cart\/change(\.js)?(\?|$)/;
  var shippingMutationSerial = 0;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isOwnMutation = !!(init && init.__kyndOwnMutation);
    if (CART_MUTATION_RE.test(url) || SHIPPING_MUTATION_RE.test(url)) shippingMutationSerial++;
    var shippingSerial = !isOwnMutation && SHIPPING_MUTATION_RE.test(url) ? shippingMutationSerial : null;

    var result;
    // Only ever attempted for the exact shape Dawn's own native request
    // actually has (a plain string URL + a JSON string body) — anything
    // else (a Request object, FormData, no body) falls straight through to
    // the unmodified call below. Fail open, never guess.
    if (!isOwnMutation && NATIVE_CHANGE_RE.test(url) && init && typeof init.body === 'string') {
      var c = cfg();
      if (c) {
        var self = this;
        var args = arguments;
        result = augmentNativeToothbrushQuantityRequest(init.body, c).then(function (augmentedBody) {
          return augmentedBody || interceptScraperQuantityIncrement(init.body, c);
        }).then(function (augmentedBody) {
          if (!augmentedBody) return nativeFetch.apply(self, args);
          var augmentedInit = {};
          for (var key in init) {
            if (Object.prototype.hasOwnProperty.call(init, key)) augmentedInit[key] = init[key];
          }
          augmentedInit.body = augmentedBody;
          return nativeFetch.call(self, input, augmentedInit);
        }).catch(function () { return nativeFetch.apply(self, args); });
      }
    }
    if (!result) result = nativeFetch.apply(this, arguments);

    if (shippingSerial !== null) {
      result.then(function (response) {
        if (response.ok === false || shippingSerial !== shippingMutationSerial) return;
        cartRead = null;
        return response.clone().json().then(function (data) {
          return Array.isArray(data.items) && Number.isFinite(data.total_price) ? data : fetchCart();
        }).then(function (cart) {
          if (shippingSerial === shippingMutationSerial) updateShippingProgress(cart);
        });
      }).catch(function () { /* Existing mutation error handling remains authoritative. */ });
    }
    if (!isOwnMutation && CART_MUTATION_RE.test(url)) {
      result
        .then(function () {
          enhanceDrawerUI();
          refresh().then(clearNativeQuantityOptimisticState, clearNativeQuantityOptimisticState);
        })
        .catch(function () {
          /* the native request's own error handling already covers the failure */
        });
    }
    return result;
  };

  function syncDrawerState() {
    var header = document.querySelector('#CartDrawer .drawer__header');
    if (header) {
      header.closest('cart-drawer').classList.toggle('is-empty', header.dataset.kyndCount === '0');
    }
  }

  // --- Optimistic native-quantity UI (display only).
  //
  // Purely visual: never sends a request, never touches fetch/network, and
  // never preventDefault()s Dawn's own click. CRITICAL: this layer never
  // WRITES to the quantity input's value under any circumstance — Dawn's
  // own <quantity-input> component already mutates it synchronously as
  // part of handling the same click (confirmed live), so an earlier
  // version of this file that also computed and wrote current+/-step here
  // stacked a second, independent +1/-1 on top of Dawn's own — a real,
  // shipped regression (one click going 1->3 instead of 1->2) — because
  // whichever handler ran second read the OTHER handler's already-updated
  // value and moved it again. Dawn/native code remains the sole owner of
  // quantity mutation; this only (a) disables that one quantity-input's
  // own +/- buttons for the duration, so a second click can't fire a
  // second request while the first is still settling, and (b) — ONLY for
  // the one transition that's commercially significant to show something
  // for (reaching 3+ boxes, the free-gift threshold) — shows a neutral,
  // unpriced "unlocking" placeholder in the upsell card, decided by
  // READING the value Dawn already produced, one tick after the click, not
  // by calculating a destination ourselves. All of it is self-healing:
  // Dawn's own native re-render replaces the whole line-item markup
  // (including this input and its buttons) with the authoritative result
  // regardless of success or failure, and the placeholder is removed as
  // soon as the SAME refresh() call the working flow already makes for
  // this signal resolves.
  var activeOptimisticQtyInput = null;
  var optimisticGiftPlaceholder = null;

  function clearOptimisticGiftPlaceholder() {
    if (optimisticGiftPlaceholder && optimisticGiftPlaceholder.parentNode) {
      optimisticGiftPlaceholder.parentNode.removeChild(optimisticGiftPlaceholder);
    }
    optimisticGiftPlaceholder = null;
  }

  function showUnlockingGiftPlaceholder() {
    var c = cfg();
    if (!c || !c.el) return;
    clearOptimisticGiftPlaceholder();
    var el = document.createElement('p');
    el.className = 'kynd-upsell__unlocking';
    el.setAttribute('role', 'status');
    el.textContent = '✓ Free Copper Tongue Scraper — unlocking…';
    c.el.hidden = false;
    c.el.insertBefore(el, c.el.firstChild);
    optimisticGiftPlaceholder = el;
  }

  function setNativeQuantityBusy(inputEl, busy) {
    var wrapper = inputEl.closest('quantity-input');
    if (!wrapper) return;
    wrapper.querySelectorAll('.quantity__button').forEach(function (btn) {
      btn.disabled = !!busy;
    });
    wrapper.classList.toggle('kynd-qty--busy', !!busy);
    inputEl.setAttribute('aria-busy', String(!!busy));
  }

  // Ends whatever optimistic state the last native quantity click started.
  // Called from the SAME refresh() invocations the two native signals
  // below already make — never a new timeout, never a guess at when the
  // real work is done.
  function clearNativeQuantityOptimisticState() {
    if (activeOptimisticQtyInput) {
      setNativeQuantityBusy(activeOptimisticQtyInput, false);
      activeOptimisticQtyInput = null;
    }
    clearOptimisticGiftPlaceholder();
  }

  // Reads (never writes) the quantity Dawn's own component has already
  // settled on for this input, one tick after the triggering click — by
  // then Dawn's own synchronous value update has already happened, so this
  // is an OBSERVATION of the real destination, never a calculation of one.
  function observeNativeQuantityResult(input) {
    var destQty = parseInt(input.value, 10);
    if (!(destQty >= 0)) return;
    if (destQty >= 3) {
      showUnlockingGiftPlaceholder();
    } else {
      clearOptimisticGiftPlaceholder();
    }
  }

  // Delegated, bubble-phase, never preventDefault()'d: this only observes
  // the same click Dawn's own quantity-input handler is also about to
  // process, and only acts when the button belongs to OUR toothbrush's
  // quantity-input (matched via the real cart variant id already on the
  // input, exactly like every other variant check in this file) — every
  // other line's stepper is left completely alone. Never reads or derives
  // a destination quantity itself — see the block comment above.
  function onNativeQuantityButtonClick(e) {
    var btn = e.target.closest('.quantity__button');
    if (!btn) return;
    var wrapper = btn.closest('quantity-input');
    var input = wrapper && wrapper.querySelector('.quantity__input');
    if (!input) return;
    var c = cfg();
    if (!c) return;
    var variantId = Number(input.getAttribute('data-quantity-variant-id'));
    if (!variantId || !matchesVariant({ variant_id: variantId }, brushIds(c))) return;

    activeOptimisticQtyInput = input;
    setNativeQuantityBusy(input, true);
    setTimeout(function () {
      observeNativeQuantityResult(input);
    }, 0);
  }

  document.addEventListener('click', onNativeQuantityButtonClick);

  function init() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(function () {
        syncDrawerState();
        renderSnapshot();
        enhanceDiscountForm();
        enhanceDiscountCollapse();
      }).observe(drawer, { childList: true, subtree: true });
    }
    syncDrawerState();
    renderSnapshot();
    enhanceDrawerUI();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 1) Native pub-sub, same mechanism <cart-drawer-items> itself uses for
  // every add/remove/quantity-change flow.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    try {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        enhanceDrawerUI();
        refresh().then(clearNativeQuantityOptimisticState, clearNativeQuantityOptimisticState);
      });
    } catch (e) {
      /* pub-sub not available under this theme version */
    }
  }

  // 2) Native DOM CustomEvent dispatched after quantity-change requests.
  document.addEventListener('cartQuantityUpdated', function () {
    enhanceDrawerUI();
    refresh().then(clearNativeQuantityOptimisticState, clearNativeQuantityOptimisticState);
  });

  // --- Header cart icon -> the EXISTING native drawer, nothing new built.
  //
  // Real DOM, re-checked directly: the clickable element is
  // <a href="{{ routes.cart_url }}" class="header__icon header__icon--cart
  // link focus-inset"> in sections/header.liquid. It has no id — the only
  // id in that markup, #cart-icon-bubble, belongs to a small child <div>
  // (just the count badge) that's a *sibling* of the cart SVG icon, not
  // the link itself. A prior version of this handler bound its click
  // listener directly to that inner div, so a click landing on the icon
  // or anywhere else inside the link's own padding never reached it and
  // fell through to the plain href navigation — which is the bug this
  // replaces.
  //
  // One delegated listener on document, added once at script load,
  // fixes that and needs no re-binding after header/cart re-renders:
  // delegation checks e.target on every click, so it keeps matching the
  // link regardless of whether the header section (or anything inside
  // it) gets swapped out and back in later.
  //
  // <cart-drawer> is the theme's own native custom element (the same one
  // wrapping #CartDrawer, defined in main.js) and its public .open()
  // method is the exact same call the native Add-to-cart flow makes
  // after a successful add — confirmed live earlier in this project by
  // calling it directly. This only ever calls that existing method; it
  // never builds a second drawer or duplicates cart markup. If the
  // element or its .open() aren't available for any reason, nothing is
  // prevented and the link's real href="/cart" runs as the fallback —
  // exactly the same graceful-degradation behavior a plain link already
  // has, no extra keydown handling needed since a real <a> already turns
  // an Enter keypress into a native click.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.header__icon--cart');
    if (!link) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    if (!link.hasAttribute('aria-haspopup')) link.setAttribute('aria-haspopup', 'dialog');
    renderSnapshot();
    drawer.open(link);
  });
})();


